import { flip, offset, shift } from '@floating-ui/dom';
import {
  trackTranslationLanguageRemoved,
  trackTranslationToggled,
  type TranslationRemovalSurface,
} from '@src/analytics/EventTracker';
import { AudioModeSegmentedToggle } from '@src/components/aiVoices/AudioModeSegmentedToggle';
import { DubRunChip } from '@src/components/aiVoices/DubRunChip';
import Badge from '@src/components/common/Badge';
import {
  ClipContextTooltip,
  DARK_TOOLTIP_STYLE,
  type TooltipEntry,
  type TooltipSection,
} from '@src/components/common/ClipContextTooltip';
import { ConfirmModal } from '@src/components/common/ConfirmModal';
import {
  clampMenuToViewport,
  ContextMenuItem,
  ContextMenuSurface,
  KebabButton,
} from '@src/components/common/contextMenuStyles';
import {
  ContextualHint,
  dismissContextualHint,
} from '@src/components/common/ContextualHint';
import ItemDropdownSelect, {
  type ItemDropdownHandle,
} from '@src/components/common/ItemDropdownSelect';
import {
  COMPACT_TOOLTIP_STYLE,
  PARAGRAPH_TOOLTIP_STYLE,
} from '@src/components/common/tooltipStyles';
import { CLIP_PREVIEW_Z } from '@src/components/content-components/clipPreviewZ';
import {
  type TranscriptOverflowEntry,
  type TranscriptOverflowItem,
  useRegisterTranscriptOverflowSection,
} from '@src/components/transcript/TranscriptOverflowExtras';
import {
  useStore,
  useTranscription,
  useTranslationStore,
  useUserIdentity,
} from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import BackIcon from '@src/svgs/BackIcon';
import CircleExclammationIcon from '@src/svgs/CircleExclammationIcon';
import DeleteIcon from '@src/svgs/DeleteIcon';
import { EditPenIcon } from '@src/svgs/EditPenIcon';
import GlobeIcon from '@src/svgs/GlobeIcon';
import InfoIcon from '@src/svgs/InfoIcon';
import MicrophoneIcon from '@src/svgs/MicrophoneIcon';
import { MicStrokeIcon } from '@src/svgs/MicStrokeIcon';
import MoreVertIcon from '@src/svgs/MoreVertIcon';
import PlusIcon from '@src/svgs/PlusIcon';
import RefreshIcon from '@src/svgs/RefreshIcon';
import { normalizeLangKey } from '@src/utility/bcp47';
import { formatLanguageCaption } from '@src/utility/languageCaption';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { observer } from 'mobx-react-lite';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactDOM, { createPortal } from 'react-dom';
import { Tooltip } from 'react-tooltip';
import styled, { createGlobalStyle, css } from 'styled-components';

import { RunChipRow } from './RunChip';
import { TranslationRunChip } from './TranslationRunChip';
import { useOutsideAlerter } from './useClickOutside';

import type { ReviewMode } from './TranslationReviewWorkspace';
import type { DubDurationLimitNote } from '@src/components/aiVoices/dubDurationLimit';
import type { DubDirectionContent } from '@src/components/aiVoices/dubInfoSummary';
import type { Language } from '@src/types.ts/language';
import type { TranslationData } from '@src/types.ts/translation';

/**
 * Wraps the dub-info overlay content and keeps react-tooltip from dismissing the
 * (clickable) overlay while the user scrolls its overflowing list with the wheel
 * (PLAT-10732).
 *
 * react-tooltip binds a bubbling `mouseout` listener on its tooltip wrapper to
 * detect the pointer leaving, then hides after `delayHide`. When the list
 * scrolls under a stationary pointer, the browser fires `mouseout`/`mouseover`
 * churn as elements shift beneath the cursor; if the scroll settles without a
 * `mouseover` re-entering the wrapper, react-tooltip treats it as a leave. We
 * intercept `mouseout` in the CAPTURE phase on the wrapper (this div's parent —
 * the element carrying react-tooltip's listener) and swallow it whenever the
 * pointer is still over the overlay, so only a genuine exit closes it.
 *
 * Known gap: dragging the native scrollbar thumb still dismisses the overlay
 * (tracked separately) — that dismissal is not via this `mouseout` path.
 */
const DubOverlayBody: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const wrapper = ref.current?.parentElement;
    if (!wrapper) return undefined;
    const onCaptureMouseOut = (e: MouseEvent) => {
      const related = e.relatedTarget;
      let stillInside = related instanceof Node && wrapper.contains(related);
      if (!stillInside && related === null) {
        // Pointer left onto the native scrollbar / nothing — fall back to the
        // geometry: is the cursor still over the overlay?
        const under = document.elementFromPoint(e.clientX, e.clientY);
        stillInside = !!under && wrapper.contains(under);
      }
      if (stillInside) {
        e.stopPropagation();
      }
    };
    wrapper.addEventListener('mouseout', onCaptureMouseOut, true);
    return () =>
      wrapper.removeEventListener('mouseout', onCaptureMouseOut, true);
  }, []);
  return <div ref={ref}>{children}</div>;
};

/**
 * Per-language dub-info overlay state (PLAT-10732). The host fetches + builds
 * the tooltip rows on first hover; the toggle just renders whatever it's given.
 */
export type DubMicInfo =
  | { status: 'loading' }
  | { status: 'error' }
  | {
      status: 'ready';
      entries: TooltipEntry[];
      /** Target-language caption, shown across the overlay's "Dub Info" title
       * row (PLAT-10732). */
      language: string;
      /** Global (whole-dub) speed-up value for the Adjustments section; `null`
       * when there was none (row omitted). */
      fullDubSpeedUp: string | null;
      /** Collapsible per-segment slid rows (PLAT-10732); empty when none. */
      slidSegments: TooltipEntry[];
      /** Collapsible per-segment sped-up rows (PLAT-10732); empty when none. */
      spedUpSegments: TooltipEntry[];
      /**
       * PLAT-11799 — what this take was asked for, how each voice model
       * resolved it, and what each line measured. `null` for a dub that was
       * never directed.
       */
      direction: DubDirectionContent | null;
    };

/** Shared react-tooltip id for every green-mic dub overlay in this toggle. */
const DUB_MIC_TOOLTIP_ID = 'translation-toggle-dub-mic';

/**
 * Floating-ui middlewares for the dub overlay. It prefers the RIGHT of the
 * dropdown (overlapping the Speaker view there is fine — PLAT-10732), but must
 * always be fully on screen (PLAT-11563): `shift` only nudges along the cross
 * axis, so with the menu near the window's right edge the overlay used to run
 * off it and clip. `flip` now falls back to the left of the mic, then
 * above/below, and `shift` keeps whichever side wins inside the viewport.
 */
const DUB_MIC_MIDDLEWARES = [
  offset(10),
  flip({
    fallbackPlacements: ['left-start', 'top-end', 'bottom-end'],
    padding: 8,
  }),
  shift({ padding: 8 }),
];

/**
 * Map a `DubMicInfo` into the `ClipContextTooltip` props for the dub-info
 * overlay — the identity rows plus the optional "Adjustments to fit voice in
 * timeline" section (global speed-up + collapsible slid / sped-up segment
 * lists). Shared by the green-mic hover overlay and the toolbar dub-actions
 * menu (PLAT-10732) so both render identical content. Loading/error collapse to
 * a single placeholder row.
 */
function dubInfoTooltipContent(info: DubMicInfo | undefined): {
  entries: TooltipEntry[];
  titleValue?: string;
  section?: TooltipSection;
  sections?: TooltipSection[];
} {
  const entries: TooltipEntry[] =
    info?.status === 'ready'
      ? info.entries
      : info?.status === 'error'
        ? [{ label: 'Dub', value: 'Details unavailable' }]
        : [{ label: 'Dub', value: 'Loading…' }];
  // The dub language sits across the "Dub Info" title row (PLAT-10732).
  const titleValue = info?.status === 'ready' ? info.language : undefined;
  // Only build the "Adjustments" section when there was actually an adjustment —
  // a global speed-up, some slid segments, or some individually sped-up
  // segments. No adjustments ⇒ omit it entirely.
  const hasAdjustments =
    info?.status === 'ready' &&
    (info.fullDubSpeedUp !== null ||
      info.slidSegments.length > 0 ||
      info.spedUpSegments.length > 0);
  const section: TooltipSection | undefined =
    info?.status === 'ready' && hasAdjustments
      ? {
          title: 'Adjustments to fit voice in timeline',
          entries: info.fullDubSpeedUp
            ? [{ label: 'Full Dub Speed-up', value: info.fullDubSpeedUp }]
            : [],
          collapsibles: [
            { label: 'Slide Segment', rows: info.slidSegments },
            { label: 'Speed-up Segment', rows: info.spedUpSegments },
          ],
        }
      : undefined;
  // PLAT-11799 — the direction sits in its own sub-section rather than inside
  // "Adjustments to fit voice in timeline": what was asked for is a different
  // kind of fact from what the layout had to do to make it fit, and folding
  // them together reads as though one caused the other.
  const info2 = info?.status === 'ready' ? info : null;
  const directionSections: TooltipSection[] = [];
  if (info2?.direction) {
    const { asked, resolved, perLine } = info2.direction;
    directionSections.push({
      title: 'Direction',
      entries: [...asked, ...resolved],
      collapsibles: [{ label: 'Line outcome', rows: perLine }],
    });
  }
  return {
    entries,
    titleValue,
    section,
    ...(directionSections.length ? { sections: directionSections } : {}),
  };
}

export interface TranslationToggleProps {
  /** Current view selection: null = "Original", else BCP 47 code. */
  selectedLanguage: string | null;

  /** Parent updates its local selectedLanguage state. null = back to Original. */
  onLanguageChange: (language: string | null) => void;

  /**
   * Fires when the user clicks "Translate" (empty state), the "+ Add
   * language" entry, or the "Regenerate" entry on a translated language.
   * Parent owns the generate modal.
   *
   * `preselectedTargetLang` is forwarded as the modal's
   * `initialTargetLanguage`. The modal flips into replace mode when that
   * language already has a translation (PLAT-9781) — so Regenerate now
   * routes through the same picker as create, with the previous method
   * pre-selected. Omitted for "+ Add language" / empty-state Translate.
   */
  onRequestGenerate: (options?: { preselectedTargetLang?: string }) => void;

  /**
   * PLAT-10261 — opt-in entry to the Translation Review workspace. When
   * provided, a "Review Translations" action appears in every translated
   * language's row menu; it receives that row's language. Omitted on surfaces
   * that don't host the workspace (captions, Creator Studio) — absent ⇒ no
   * entry, no behavior change (D9 containment).
   *
   * PLAT-10267: additionally gated behind the `enableTranslationReview`
   * LaunchDarkly flag (dark launch).
   */
  onRequestReview?: (language: string, mode: ReviewMode) => void;

  /**
   * PLAT-10596 (DE8) — opt-in dub entry. When provided, every translated row's
   * menu offers "Dub with AI Voice" (no dub yet) or "Re-dub" (has one), and the
   * header's Voice slot offers the first dub of the SELECTED language. Receives
   * the language to dub. The parent gates the callback on
   * `enableAiVoices && dubbingEnabled`; absent ⇒ no entry.
   */
  onRequestDub?: (language: string) => void;

  /**
   * PLAT-11440 — opt-in "Dub with AI Voice" header button for the selected
   * language, wired on its own.
   *
   * `onRequestDub` drives the header button AND the per-row Dub / Re-dub menu
   * entries, which redo the whole STORY's dub. Clip Preview wants only the
   * button — it offers a FIRST dub of one clip — so it wires this instead and
   * gets no row entries. When both are given this one takes the button.
   */
  onRequestFirstDub?: (language: string) => void;
  /**
   * PLAT-11663 — "Regenerate Dub" in a DUBBED row's menu only, for a surface
   * that offers no whole-story first dub there (the clip preview: its first
   * dub is the Voice-slot button, one clip at a time, PLAT-11440). Ignored
   * when `onRequestDub` is given — that one already covers both entries.
   */
  onRequestRegenerateDub?: (language: string) => void;

  /**
   * PLAT-11532 — why a dub cannot start here, when it cannot. While set, the
   * header's "Dub with AI Voice" button and every row menu's Dub / Re-dub entry
   * are inert: the note's one-line fact paints as a warning under the Voice
   * slot and its way-out sentence is the hover tooltip on each inert control
   * (PLAT-11589). The host works it out (the story's or the clip's length
   * against the LaunchDarkly cap) because only the host knows which length
   * applies; this component only paints it.
   */
  dubUnavailableNote?: DubDurationLimitNote;

  /**
   * PLAT-10617 (T14, DE8) — opt-in "Remove Dub" row-menu entry, shown for a
   * language that already has a dub (`dubbedLanguages` includes it). Receives
   * that language; the parent confirms + runs the backend soft-delete. Gated by
   * the parent on `enableAiVoices && dubbingEnabled`.
   */
  onRequestRemoveDub?: (language: string) => void;
  /**
   * PLAT-11877 — "Remove Language" removes the translation AND its dub: a dub
   * is made from the translation and cannot outlive it (an orphaned dub was
   * PLAT-11704's root cause). The host hands the dub removal itself
   * (`useRemoveDub().remove`, no confirm of its own); the toggle runs it
   * before the translation and stops if it failed. Gated like
   * `onRequestRemoveDub`; absent, a dubbed language's removal leaves the dub
   * — the old behaviour, and the confirm copy then says only "translation".
   */
  onRemoveDubForLanguage?: (
    language: string,
    options?: { silent?: boolean },
  ) => Promise<boolean>;

  /**
   * PLAT-10597 (DE6) — Original↔Dubbed audio mode for the selected language.
   * When `onAudioModeChange` is wired (dubbing-enabled surfaces), a segmented
   * "Original / Dubbed / Both" toggle renders at the TOP of the dropdown menu,
   * above the language list, while a non-original language is selected. Absent ⇒
   * no toggle (unaffected on other surfaces). `both` plays the original and the
   * dub simultaneously. Playback wiring is the rest of T9; this is the control
   * shell + state.
   */
  audioMode?: 'original' | 'dubbed' | 'both';
  onAudioModeChange?: (mode: 'original' | 'dubbed' | 'both') => void;

  /**
   * The run `onAudioModeChange` started has not finished. While set the Voice
   * toggle paints as working and cannot be pressed, so nobody starts a second
   * run over the top of the first.
   */
  audioModeBusy?: boolean;

  /**
   * The language whose dub is applied to the clip this dropdown belongs to, or
   * `null`/absent when none is.
   *
   * Keeps the Voice toggle on screen whatever language the transcript shows.
   * Without it the toggle appears only for a selected language that has a dub,
   * so switching the transcript to an undubbed language hid the only control
   * over a dub that was still playing.
   */
  appliedDubLanguage?: string | null;

  /**
   * PLAT-10622 — optional control rendered directly under the Original↔Dubbed
   * voice toggle in the dropdown header (the voice/music Separate control,
   * DE5). Only surfaces that own it (Transcript & Speakers) pass it; others
   * leave it undefined so the header is unchanged. Renders only alongside the
   * audio toggle — i.e. a non-original, already-dubbed language on a
   * dubbing-enabled surface. On an undubbed language the header shows a Dub
   * button instead and the music line is hidden (PLAT-10664).
   */
  musicControl?: React.ReactNode;
  /**
   * PLAT-11877 — the host shows the Voice and Music pickers beside its player
   * (`PlayerAudioPickers`), so the reading language's block carries no Voice
   * toggle and no Music line: the pickers say how the dub sounds. What stays
   * is about the dub's STATE rather than its playback — "Dub with AI Voice"
   * for a language without one (with the cap note), and the coverage notice.
   * The audio props are still read: the mode-follows-language sync runs here.
   */
  audioControlsInPlayer?: boolean;

  /**
   * PLAT-11899 — the Dub Level row, directly under the Voice toggle and above
   * the music line. A slot like `musicControl`: the host owns the level and
   * whether it applies (it is omitted on the Original voice), so the header
   * only decides where it goes.
   */
  dubLevelControl?: React.ReactNode;

  /**
   * The "part of this clip will be silent" warning, shown under the voice
   * toggle. A slot rather than props for the same reason `musicControl` is one:
   * only a surface that owns a clip can work out its coverage.
   *
   * PLAT-11585 — may be a function of the menu, so the notice's own action
   * ("Dub it") can close this menu the way every other action in it does.
   * Left open, the menu sat on top of the dub progress modal it had launched.
   */
  coverageNotice?:
    | React.ReactNode
    | ((menu: { close: () => void }) => React.ReactNode);

  /**
   * PLAT-10621 — BCP-47 codes of languages that already have a generated dub.
   * Each matching language row shows a green mic. Absent/empty ⇒ no mics.
   */
  dubbedLanguages?: string[];

  /**
   * PLAT-10732 — per-language dub-info overlay rows, keyed by BCP-47 code.
   * Populated lazily by the host as the user hovers a green mic
   * (`onDubInfoHover`). Absent ⇒ the mic shows a plain "Has a dub" title.
   */
  dubInfo?: Record<string, DubMicInfo>;

  /**
   * PLAT-10732 — called when the user hovers a language's green mic, so the
   * host can fetch + cache that dub's manifest and populate `dubInfo`.
   */
  onDubInfoHover?: (code: string) => void;

  /**
   * Hide both the "+ Add language" and "Regenerate" entries. Set when the
   * parent surface doesn't own a generate modal (e.g. captions toolbar
   * where generation lives on the transcript panel) or when the modal is
   * already open (re-entrancy guard).
   */
  generateDisabled?: boolean;

  /**
   * Showcase-enabled languages — the same `Language[]` Brand Kit uses. Source
   * of captions for both the dropdown trigger label and option list.
   */
  supportedLanguages: Language[];

  /**
   * Opt-in for the language header (selected language + code box) on surfaces
   * that DON'T (yet) wire dubbing. The header otherwise renders automatically
   * whenever `onAudioModeChange` is present. Set on the Clip Producer overlay,
   * which has no voice/dub/music controls yet — the Voice row stays hidden
   * until `onAudioModeChange` is wired there.
   */
  showLanguageHeader?: boolean;

  /**
   * Where this toggle is mounted. Forwarded to analytics on Remove so
   * product can slice removals by entry point.
   */
  surface: TranslationRemovalSurface;
  /**
   * PLAT-11877 — how the control is shown (the YouTube Studio model: a
   * language is a place you go, not a toolbar filter).
   *  - `dropdown` (default): the compact chip whose menu lists the languages.
   *  - `button`: a globe with the language count, for the toolbar; opens the
   *    host's Languages panel through `onOpenPanel`.
   *  - `panel`: the Languages list itself, filling the transcript pane — the
   *    dropdown's menu (the reading language's block with its Voice and Music,
   *    then every other language with its actions) given the whole pane.
   *  - `title`: the line naming the translation being read, with Review and a
   *    way back to the original. Renders nothing on the original.
   * Every presentation shares one set of props, so a host builds the element
   * once and clones it per place.
   */
  presentation?: 'dropdown' | 'button' | 'panel' | 'title';
  /** `button`: open the host's Languages panel. */
  onOpenPanel?: () => void;
  /** `panel`: close it (back to the transcript). */
  onClosePanel?: () => void;
  /**
   * `panel`: show a back arrow in its heading (default). Off where a tab bar
   * is already the way back (the clip preview's Languages tab).
   */
  panelBack?: boolean;
  /**
   * PLAT-11877 — with no translation yet, point a coach mark at Add
   * Translation saying what it does (the Transcript & Languages tab, while
   * it is the open tab). Remembered per story: each story nudges once, and
   * closing it, or adding the translation, puts it away for that story.
   */
  firstTranslationHint?: boolean;
}

/**
 * The coach mark's key carries the story id: the lesson is per story (a new
 * story is a new decision), not per browser. No story id ⇒ one shared key.
 */
export const firstTranslationHintKey = (storyId: string | null | undefined) =>
  storyId ? `add-first-translation:${storyId}` : 'add-first-translation';

const ORIGINAL_VALUE = '__original__';

/**
 * react-tooltip ids for the hover hints inside the language menu (PLAT-11563):
 * one-line hints on the Voice / Music segments and the music kebab, and the
 * sentence-long help on "Separate". The toggle renders both <Tooltip>s, lifted
 * above the menu; hosts pass the ids into the `musicControl` they slot in.
 */
export const LANGUAGE_MENU_TOOLTIP_ID = 'translation-menu-control-tooltip';
/** PLAT-11690 — hover on the generate entries while a translation runs. */
export const TRANSLATION_RUNNING_NOTE = 'A translation is already running';
/** PLAT-11979 — the reason a dub-only row is inert for an undubbed language. */
export const NO_DUB_YET_NOTE = 'Dub this language first';
/** PLAT-11979 — a first dub covers the language being read, so switch to it. */
export const SWITCH_LANGUAGE_FIRST_NOTE = 'Switch to this language first';
export const READING_LANGUAGE_NOTE = 'You are reading this language';
export const LANGUAGE_MENU_HELP_TOOLTIP_ID = 'translation-menu-help-tooltip';

/**
 * What is playing, for the trigger tooltip. Spelled out as VOICE, because a
 * bare "Original" next to a language name reads as "the original language" —
 * on an undubbed Italian transcript it wrongly said the original was Italian.
 */
const AUDIO_MODE_LABELS: Record<'original' | 'dubbed' | 'both', string> = {
  original: 'Original voice',
  dubbed: 'Dubbed voice',
  both: 'Original and dubbed voices',
};

/**
 * PLAT-10611 — a translation generated before the stable-id migration carries
 * only `originalIndices` on its phrases, never `originalElementIds`. Those blobs
 * join to the transcript by integer index alone, which breaks when a clip reuses
 * the same source span, so the user should regenerate them. Stale = the
 * translation has phrases but not one carries a non-empty `originalElementIds`
 * (a fresh translation stamps ids on every content phrase, so a single id
 * anywhere means it is post-migration).
 */
export function isTranslationStale(data: TranslationData | undefined): boolean {
  if (!data || data.phrases.length === 0) return false;
  return !data.phrases.some(
    p => p.originalElementIds !== undefined && p.originalElementIds.length > 0,
  );
}

export const TranslationToggle: React.FC<TranslationToggleProps> = observer(
  function TranslationToggle({
    selectedLanguage,
    onLanguageChange,
    onRequestGenerate,
    onRequestReview,
    onRequestDub,
    onRequestRegenerateDub,
    onRequestFirstDub,
    dubUnavailableNote,
    onRequestRemoveDub,
    onRemoveDubForLanguage,
    audioMode = 'original',
    onAudioModeChange,
    audioModeBusy,
    appliedDubLanguage,
    musicControl,
    dubLevelControl,
    coverageNotice,
    audioControlsInPlayer = false,
    dubbedLanguages,
    dubInfo,
    onDubInfoHover,
    generateDisabled,
    showLanguageHeader,
    supportedLanguages,
    surface,
    presentation = 'dropdown',
    onOpenPanel,
    onClosePanel,
    panelBack = true,
    firstTranslationHint = false,
  }) {
    const translationStore = useTranslationStore();
    const transcription = useTranscription();
    const rootStore = useStore();
    const videoCreator = useVideoCreatorStore();
    const userIdentity = useUserIdentity();
    const { enableTranslationReview } = useFlagsCombination(
      userIdentity.currentRole,
    );
    const scrollRef = useRef<HTMLDivElement>(null);
    const [isRemoving, setIsRemoving] = useState(false);
    // The trigger's tooltip hides while the menu is open (PLAT-11563), the way
    // the toolbar's ⋯ menu hides "More actions" — otherwise it sits over the
    // menu's header.
    const [menuOpen, setMenuOpen] = useState(false);
    const [confirmRemoveLang, setConfirmRemoveLang] = useState<string | null>(
      null,
    );
    // PLAT-11979 — this toggle's languages, offered a second time as a section
    // of the transcript's ⋯ menu (set below, once the rows are known; stays
    // null on a surface without the provider, or with nothing to list). Only
    // the toolbar instance registers: it renders the confirm and Dub Info its
    // rows open, which the title instance does not.
    const overflowSection = useRegisterTranscriptOverflowSection(
      'languages',
      presentation === 'dropdown' || presentation === 'button',
    );
    overflowSection.current = null;

    // PLAT-10732: a kebab action (Re-dub / Dub info) dismisses the language
    // dropdown via this imperative handle. The dub-info popover is lifted OUT of
    // the kebab (which unmounts with the dropdown) to a fixed-position portal
    // here, so it survives the dropdown closing.
    const dropdownRef = useRef<ItemDropdownHandle>(null);
    const [dubInfoTarget, setDubInfoTarget] = useState<{
      code: string;
      x: number;
      y: number;
    } | null>(null);
    const dubInfoRef = useRef<HTMLDivElement>(null);
    const closeDubInfo = useCallback(() => setDubInfoTarget(null), []);
    useOutsideAlerter(dubInfoRef, closeDubInfo);
    useEffect(() => {
      if (!dubInfoTarget) return undefined;
      const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') closeDubInfo();
      };
      document.addEventListener('keydown', onKey);
      return () => document.removeEventListener('keydown', onKey);
    }, [dubInfoTarget, closeDubInfo]);
    // Close a lingering info popover if the active language changes.
    useEffect(() => closeDubInfo(), [selectedLanguage, closeDubInfo]);

    const runRemove = async (lang: string) => {
      // Re-entrancy guard — prevents a fast double-confirm from firing
      // duplicate analytics events on what is logically one user action.
      // (The store's eviction path is itself idempotent; this is purely
      // about analytics fidelity.)
      if (isRemoving) return;
      setIsRemoving(true);
      try {
        // The dub first, so a failure there leaves the language whole rather
        // than a dub with no translation under it.
        const withDub = !!(
          dubbedLanguages?.includes(lang) && onRemoveDubForLanguage
        );
        if (withDub) {
          const dubRemoved = await onRemoveDubForLanguage(lang, {
            silent: true,
          });
          if (!dubRemoved) return;
        }
        await translationStore.removeLanguage(lang);
        trackTranslationLanguageRemoved({
          story_id: rootStore.storyId ?? undefined,
          language: lang,
          surface,
        });
        // Same shape as the dub's own "<Language> dub removed" toast; the
        // silent dub removal above folds into this one line.
        videoCreator.showToast({
          state: 'success',
          message: `${byCode.get(lang)?.caption ?? lang} ${
            withDub ? 'translation and dub' : 'translation'
          } removed`,
        });
      } catch (err) {
        const message =
          err instanceof Error && err.message
            ? err.message
            : 'Remove failed. Try again.';
        // The toast carries the real reason — the chip used to hide it behind a
        // hover tooltip.
        videoCreator.showToast({ state: 'error', message });
      } finally {
        setIsRemoving(false);
      }
    };

    const changeLanguage = (next: string | null) => {
      const current = selectedLanguage;
      if (next === current) {
        onLanguageChange(next);
        return;
      }
      const storyId = rootStore.storyId ?? undefined;
      if (next) {
        trackTranslationToggled({
          story_id: storyId,
          language: next,
          action: 'on',
          view: 'transcript',
        });
      } else if (current) {
        trackTranslationToggled({
          story_id: storyId,
          language: current,
          action: 'off',
          view: 'transcript',
        });
      }
      onLanguageChange(next);
    };

    const byCode = useMemo(() => {
      const m = new Map<string, Language>();
      for (const l of supportedLanguages) m.set(l.value, l);
      return m;
    }, [supportedLanguages]);

    // Does the language the reader has SELECTED have a dub of its own? Hoisted
    // above the early return below so the effect that follows can be a hook.
    const selectedHasDub =
      !!selectedLanguage &&
      (dubbedLanguages?.includes(selectedLanguage) ?? false);

    // What is HEARD always follows what is READ (ticket 49, 2026-08-31).
    //
    // The language being heard is stored on the clip and does not move when the
    // transcript moves, so the two drift apart: a Russian dub goes on playing
    // under a transcript switched to Spanish. This effect closes that gap. The
    // playing dub language ends up either equal to the selected language, or
    // nothing at all — never some third language the reader did not choose.
    //
    // This replaces the narrower PLAT-11440 rule, which acted only when the
    // selected language had no dub anywhere in the story. That question is the
    // wrong size: one dubbed clip puts a language on the story's list, so the
    // rule stood down on every OTHER clip, which is exactly where the wrong
    // language was being heard.
    //
    // The rule runs both ways (PLAT-11593). Until then it only ever moved a
    // clip OFF a dub or BETWEEN dubs; it never turned one on. Opening a clip
    // from the grid brings no language, so the clip was put back on its
    // original voice and saved that way — and choosing the dubbed language
    // afterwards changed the transcript and nothing else. Now a language
    // SWITCH onto a language that has a dub puts the voice on it, the way the
    // Transcript & Speakers surface has done since PLAT-10719. Only a switch:
    // the language the effect last saw is kept below so a press back to
    // Original on a dubbed language is respected, and a clip saved on Original
    // is not flipped when it merely opens.
    const lastSelectedLanguageRef = useRef<string | null | undefined>(
      undefined,
    );
    // PLAT-11877 — one instance keeps what is heard in step with what is read:
    // the toolbar one (dropdown / button), which is always mounted. The panel
    // and title instances would otherwise each apply the same correction.
    const ownsAudioSync =
      presentation === 'dropdown' || presentation === 'button';
    useEffect(() => {
      if (!onAudioModeChange || !ownsAudioSync) return;
      // `audioModeBusy` means the host is still reading which languages have a
      // dub, so `dubbedLanguages` cannot be trusted yet. Acting on it here would
      // undo the host's own work: a first dub refreshes that list and puts the
      // clip on the new dub in one go, and moving in the gap before the list
      // arrives would silence the dub that was just made. It also keeps this
      // from firing on first paint, before the list has ever loaded.
      if (audioModeBusy) return;
      const languageSwitched =
        lastSelectedLanguageRef.current !== undefined &&
        lastSelectedLanguageRef.current !== selectedLanguage;
      lastSelectedLanguageRef.current = selectedLanguage;
      // The language being read has a dub and nothing dubbed is playing: hear
      // it. The host writes the selected language, since it has a dub.
      if (languageSwitched && audioMode === 'original' && selectedHasDub) {
        onAudioModeChange('dubbed');
        return;
      }
      // Nothing dubbed is playing, so nothing can be wrong.
      if (audioMode === 'original') return;
      if (!appliedDubLanguage) return;
      // Already consistent. This is also what makes a first dub safe: the host
      // puts the clip straight onto its own new language, so the two match and
      // this effect leaves it alone.
      if (appliedDubLanguage === selectedLanguage) return;

      if (selectedHasDub) {
        // The selected language has a dub somewhere in the story. Move the clip
        // onto it rather than silencing: passing the mode back re-writes the dub
        // options with the selected language, because the host chooses the
        // language a press acts on. Where that dub covers this clip it is heard;
        // where it does not, the original voice returns on its own.
        onAudioModeChange(audioMode);
        return;
      }

      // No dub to move to — either the selected language has none, or the
      // transcript is on Original. Back to the original voice. On Original this
      // reverses PLAT-10628, which kept the foreign dub playing so its toggle
      // stayed useful; under the new rule that dub does not play, so the toggle
      // has nothing left to govern.
      onAudioModeChange('original');
    }, [
      onAudioModeChange,
      audioModeBusy,
      selectedLanguage,
      selectedHasDub,
      appliedDubLanguage,
      audioMode,
    ]);

    if (supportedLanguages.length === 0) {
      return null;
    }

    const translatedCodes = Array.from(
      translationStore.translations.keys(),
    ).sort((a, b) => {
      const labelA = byCode.get(a)?.caption ?? formatLanguageCaption(a);
      const labelB = byCode.get(b)?.caption ?? formatLanguageCaption(b);
      return labelA.localeCompare(labelB);
    });
    // PLAT-11690 — one translation at a time. While one runs, the entries that
    // would start another (Translate / "+ Add language" / Regenerate) stay
    // listed but inert, and say why on hover, instead of silently abandoning
    // the run in flight (the PLAT-11603 rule: inert with a reason, not hidden).
    const translationRunning =
      translationStore.activeRun !== null &&
      translationStore.activeRun.storyId === rootStore.storyId;
    const generateInertNote = translationRunning
      ? TRANSLATION_RUNNING_NOTE
      : undefined;
    // The Languages panel's heading, shared by the empty and the listed panel.
    const languagesPanelHead = (
      <LanguagesPanelHead>
        {panelBack && (
          <TitleBack
            type="button"
            aria-label="Back to the transcript"
            onClick={() => onClosePanel?.()}
          >
            <BackIcon
              width="14"
              height="10"
              strokeColor="currentColor"
              strokeWidth="2"
            />
          </TitleBack>
        )}
        <LanguagesPanelTitle>Languages</LanguagesPanelTitle>
      </LanguagesPanelHead>
    );

    // The Languages tab is where a run is started from, and while it is open
    // the toolbar (and the chip beside its globe) is not on screen — so the
    // tab shows the run itself, in both its states. The row hides itself
    // while no chip renders (`:empty`), so an idle tab carries no blank row.
    const languagesPanelRun = (
      <LanguagesPanelRun data-testid="languages-panel-run">
        <DubRunChip />
        <TranslationRunChip />
      </LanguagesPanelRun>
    );

    const originalCode = transcription?.language ?? 'original';
    const readingCaption = selectedLanguage
      ? (byCode.get(selectedLanguage)?.caption ??
        formatLanguageCaption(selectedLanguage))
      : null;
    // The toolbar's language control (PLAT-11877): the globe and the code of
    // the language being read — the toolbar states the language at all
    // times; the Languages tab is the discovery. Its one job is to open that
    // tab, translations or not.
    const languagesButton = (
      <>
        <TranslateButton
          type="button"
          aria-label="Languages"
          onClick={() => onOpenPanel?.()}
          data-testid="languages-button"
          data-tooltip-id="translation-toggle-tooltip"
          data-tooltip-content={
            readingCaption
              ? `Reading ${readingCaption} · Open the Languages tab`
              : 'Open the Languages tab'
          }
        >
          <TranslateIcon aria-hidden="true">
            <GlobeIcon width="16" height="16" />
          </TranslateIcon>
          <ToolbarLanguageCode>
            {normalizeLangKey(selectedLanguage ?? originalCode)}
          </ToolbarLanguageCode>
        </TranslateButton>
        <RunChipRow>
          <DubRunChip />
          <TranslationRunChip />
        </RunChipRow>
        {ReactDOM.createPortal(
          <Tooltip
            id="translation-toggle-tooltip"
            style={TRANSLATION_TOOLTIP_STYLE}
            place="bottom"
            positionStrategy="fixed"
          />,
          document.body,
        )}
      </>
    );

    if (translatedCodes.length === 0) {
      if (presentation === 'title') return null;
      // One button, one behaviour: the globe opens the Languages tab, whose
      // empty state offers the first translation.
      if (presentation === 'button' && onOpenPanel) return languagesButton;
      // No translation yet: the panel says so and offers the first one.
      if (presentation === 'panel') {
        return (
          <LanguagesPanel data-testid="languages-panel">
            {panelBack && languagesPanelHead}
            <LanguagesPanelEmpty>
              <span>This transcript has no translations yet.</span>
              {languagesPanelRun}
              <HeaderDubButton
                type="button"
                aria-disabled={generateInertNote ? true : undefined}
                data-tooltip-id="translation-toggle-tooltip"
                data-tooltip-content={generateInertNote}
                disabled={generateDisabled}
                onClick={
                  generateInertNote ? undefined : () => onRequestGenerate()
                }
              >
                <PlusIcon
                  width="14"
                  height="14"
                  strokeColor="currentColor"
                  strokeWidth={4}
                />
                Add Translation
              </HeaderDubButton>
            </LanguagesPanelEmpty>
          </LanguagesPanel>
        );
      }
      return (
        <>
          {/* PLAT-11877 — a story's first translation is the step users
              most often could not find as a bare globe; the button says what
              it does, and on the story tab a coach mark under it says what
              happens next, once per story. The tooltip is left to the
              "already running" note. */}
          <ContextualHint
            hintKey={firstTranslationHintKey(rootStore.storyId)}
            show={firstTranslationHint && !translationRunning}
            text="No translations yet. Add Translation writes this transcript in another language, ready to review, dub and clip from here."
            testId="first-translation-hint"
          >
            <TranslateButton
              type="button"
              onClick={
                generateInertNote
                  ? undefined
                  : () => {
                      // Acting on the control ends the lesson (design system).
                      dismissContextualHint(
                        firstTranslationHintKey(rootStore.storyId),
                      );
                      onRequestGenerate();
                    }
              }
              disabled={generateDisabled}
              aria-disabled={generateInertNote ? true : undefined}
              data-tooltip-id="translation-toggle-tooltip"
              data-tooltip-content={generateInertNote}
            >
              <TranslateIcon aria-hidden="true">
                <GlobeIcon width="16" height="16" />
              </TranslateIcon>
              <TranslateLabel>Add Translation</TranslateLabel>
            </TranslateButton>
          </ContextualHint>
          {/* PLAT-11690 — a story's FIRST translation runs with no language
              list to sit beside, and this branch returns before the toolbar
              below ever renders. Without the chip here the most common run of
              all — the first one — would show nothing at all. */}
          <RunChipRow>
            <TranslationRunChip />
          </RunChipRow>
          {ReactDOM.createPortal(
            <Tooltip
              id="translation-toggle-tooltip"
              style={TRANSLATION_TOOLTIP_STYLE}
              place="bottom"
              positionStrategy="fixed"
            />,
            document.body,
          )}
        </>
      );
    }

    const hasUnfilledLanguages = supportedLanguages.some(
      l => !translatedCodes.includes(l.value),
    );

    // Dropdown rows show the full language name on the left and the BCP
    // 47 code right-aligned. The TRIGGER (selected label) renders only
    // the compact chip (globe + code) — `ItemDropdownSelect` always
    // renders the `caption` in both places, so we use a hybrid node that
    // collapses to just the chip when the option is rendered inside the
    // trigger, and expands to the full row when inside the menu.
    const inPanel = presentation === 'panel';
    const showHeader = inPanel || !!onAudioModeChange || !!showLanguageHeader;
    const showReview = enableTranslationReview && !!onRequestReview;
    const canRegenerate = !generateDisabled;
    // Listed but inert while a translation runs (PLAT-11690).
    const regenerateDisabledNote = generateInertNote;
    // Every row action opens another surface (a modal, the review workspace, a
    // confirm), so each dismisses the dropdown once it has fired.
    // In the Languages panel an action that opens another surface closes the
    // panel too (back to the transcript underneath). `keepOpen` is for the two
    // surfaces this component itself owns — the remove confirm and the dub-info
    // popover — which would unmount with the panel.
    const closeDropdown = () =>
      inPanel ? onClosePanel?.() : dropdownRef.current?.close();
    const closeMenuKeepPanel = () => {
      if (!inPanel) dropdownRef.current?.close();
    };

    // PLAT-11662 — the two review entries a language row carries (pencil and
    // mic), as closures the list rows and the header row share.
    const reviewTranslationFor = (code: string) =>
      showReview && onRequestReview
        ? () => {
            onRequestReview(code, 'translation');
            closeDropdown();
          }
        : undefined;
    const reviewDubFor = (code: string, hasDub: boolean) =>
      showReview && hasDub && onRequestReview
        ? () => {
            onRequestReview(code, 'dubbing');
            closeDropdown();
          }
        : undefined;

    const regenerateFor = (code: string) =>
      canRegenerate
        ? () => {
            onRequestGenerate({ preselectedTargetLang: code });
            closeDropdown();
          }
        : undefined;

    // One language's actions kebab, used by every row in the "Switch To" list
    // AND by the header for the language being read (whose row is not in the
    // list) — so each language has exactly one kebab, wherever it is shown.
    const rowActionsFor = (code: string, label: string, hasDub: boolean) => (
      <LanguageRowActionsMenu
        label={label}
        hasDub={hasDub}
        dubDisabledNote={dubUnavailableNote?.tooltip}
        regenerateDisabledNote={regenerateDisabledNote}
        onDub={
          onRequestDub
            ? () => {
                onRequestDub(code);
                closeDropdown();
              }
            : onRequestRegenerateDub && hasDub
              ? () => {
                  onRequestRegenerateDub(code);
                  closeDropdown();
                }
              : undefined
        }
        onDubInfo={
          hasDub && onDubInfoHover
            ? pos => {
                onDubInfoHover(code);
                setDubInfoTarget({ code, ...pos });
                closeMenuKeepPanel();
              }
            : undefined
        }
        onReviewTranslation={
          showReview && onRequestReview
            ? () => {
                onRequestReview(code, 'translation');
                closeDropdown();
              }
            : undefined
        }
        onReviewDub={
          showReview && hasDub && onRequestReview
            ? () => {
                onRequestReview(code, 'dubbing');
                closeDropdown();
              }
            : undefined
        }
        onRegenerate={regenerateFor(code)}
        onRemoveDub={
          hasDub && onRequestRemoveDub
            ? () => {
                onRequestRemoveDub(code);
                closeDropdown();
              }
            : undefined
        }
        onRemove={() => {
          setConfirmRemoveLang(code);
          closeMenuKeepPanel();
        }}
      />
    );

    // PLAT-11979 — the kebab's actions for one language, as rows of the ⋯
    // menu's Languages submenu. Same order as the kebab; where the kebab omits
    // an action the state blocks (no dub yet), the submenu lists it inert with
    // the reason, so every language's submenu has the same shape on a surface.
    // Only a surface gate (no handler wired, flag off) drops a row.
    const overflowActionsFor = (
      code: string,
      hasDub: boolean,
    ): TranscriptOverflowItem[] => {
      const rows: TranscriptOverflowItem[] = [];
      // Stays open on the switched-to language, so its now-live Dub is one
      // click away; the row stays too, inert, so nothing shifts under the
      // pointer.
      rows.push({
        key: 'switch-language',
        label: 'Switch to This Language',
        icon: <GlobeIcon width="13" height="13" />,
        disabledNote:
          code === selectedLanguage ? READING_LANGUAGE_NOTE : undefined,
        keepOpen: true,
        onClick: () => changeLanguage(code),
      });
      if (showReview && onRequestReview) {
        rows.push(
          {
            key: 'review-translation',
            label: 'Review Translation',
            icon: <EditPenIcon size={13} />,
            onClick: () => onRequestReview(code, 'translation'),
          },
          {
            key: 'review-dub',
            label: 'Review Dub',
            icon: <MicStrokeIcon size={13} />,
            disabledNote: hasDub ? undefined : NO_DUB_YET_NOTE,
            onClick: () => onRequestReview(code, 'dubbing'),
          },
        );
      }
      if (canRegenerate) {
        rows.push({
          key: 'regenerate-translation',
          label: 'Regenerate Translation',
          icon: (
            <RefreshIcon width="13" height="13" strokeColor="currentColor" />
          ),
          disabledNote: regenerateDisabledNote,
          onClick: () => onRequestGenerate({ preselectedTargetLang: code }),
        });
      }
      // The story tab re-dubs the whole story (`onRequestDub`); the clip
      // preview and Creator Studio dub a language first from the Voice slot
      // (`onRequestFirstDub`) and re-dub one that has a dub. A first dub runs
      // over the language being read, so another language's row stays inert.
      const firstDub =
        code === selectedLanguage ? onRequestFirstDub : undefined;
      const dubAction = hasDub
        ? (onRequestDub ?? onRequestRegenerateDub)
        : (onRequestDub ?? firstDub);
      if (onRequestDub || onRequestRegenerateDub || onRequestFirstDub) {
        rows.push({
          key: 'dub',
          label: hasDub ? 'Regenerate Dub' : 'Dub with AI Voice',
          icon: (
            <MicrophoneIcon width="13" height="13" fillColor="currentColor" />
          ),
          disabledNote:
            dubUnavailableNote?.tooltip ??
            (dubAction
              ? undefined
              : !hasDub && onRequestFirstDub
                ? SWITCH_LANGUAGE_FIRST_NOTE
                : NO_DUB_YET_NOTE),
          onClick: () => dubAction?.(code),
        });
      }
      if (onDubInfoHover) {
        rows.push({
          key: 'dub-info',
          label: 'Dub Info',
          icon: <InfoIcon />,
          disabledNote: hasDub ? undefined : NO_DUB_YET_NOTE,
          onClick: rowRect => {
            onDubInfoHover(code);
            const pos = clampMenuToViewport(
              rowRect.left,
              rowRect.bottom + 4,
              DUB_INFO_WIDTH,
              DUB_INFO_HEIGHT,
            );
            setDubInfoTarget({ code, ...pos });
          },
        });
      }
      rows.push({
        key: 'remove-language',
        label: 'Remove Language',
        icon: <DeleteIcon width="13" height="13" />,
        danger: true,
        onClick: () => setConfirmRemoveLang(code),
      });
      if (onRequestRemoveDub) {
        rows.push({
          key: 'remove-dub',
          label: 'Remove Dub',
          icon: <DeleteIcon width="13" height="13" />,
          danger: true,
          disabledNote: hasDub ? undefined : NO_DUB_YET_NOTE,
          onClick: () => onRequestRemoveDub(code),
        });
      }
      return rows;
    };
    const overflowEntries: TranscriptOverflowEntry[] = translatedCodes.map(
      code => {
        const hasDub = dubbedLanguages?.includes(code) ?? false;
        const stale = isTranslationStale(
          translationStore.translations.get(code),
        );
        return {
          kind: 'submenu',
          key: `language:${code}`,
          label: byCode.get(code)?.caption ?? formatLanguageCaption(code),
          meta: [code, hasDub && 'Dubbed', stale && 'Needs Update']
            .filter(Boolean)
            .join(' · '),
          testId: `transcript-overflow-language-${code}`,
          items: overflowActionsFor(code, hasDub),
        };
      },
    );
    if (!generateDisabled) {
      overflowEntries.push({
        key: 'add-translation',
        label: 'Add Translation',
        icon: <PlusIcon width="13" height="13" />,
        disabledNote: generateInertNote,
        testId: 'transcript-overflow-add-translation',
        onClick: () => onRequestGenerate(),
      });
    }
    if (overflowEntries.length > 0) {
      overflowSection.current = {
        key: 'languages',
        label: 'Languages',
        entries: overflowEntries,
      };
    }

    // PLAT-11563: actions live on the language ROW, not on the selection. A
    // translated row carries its own kebab (regenerate / review / dub / remove
    // for THAT language), so nothing requires switching the transcript to a
    // language first. The Original row has nothing to act on.
    const values: {
      caption: React.ReactNode;
      value: string | null;
      isAction?: boolean;
    }[] = [
      {
        caption: (
          <LanguageOption
            code={originalCode}
            label={
              byCode.get(originalCode)?.caption ??
              formatLanguageCaption(originalCode)
            }
            isOriginal
            selected={!selectedLanguage}
          />
        ),
        value: ORIGINAL_VALUE,
      },
      ...translatedCodes.map(code => {
        const label = byCode.get(code)?.caption ?? formatLanguageCaption(code);
        const hasDub = dubbedLanguages?.includes(code) ?? false;
        return {
          caption: (
            <LanguageOption
              code={code}
              label={label}
              hasDub={hasDub}
              stale={isTranslationStale(
                translationStore.translations.get(code),
              )}
              selected={code === selectedLanguage}
              onDubHover={
                onDubInfoHover ? () => onDubInfoHover(code) : undefined
              }
              onReviewTranslation={reviewTranslationFor(code)}
              onReviewDub={reviewDubFor(code, hasDub)}
              onRegenerate={regenerateFor(code)}
              actions={rowActionsFor(code, label, hasDub)}
            />
          ),
          value: code,
        };
      }),
    ];

    // Header (PLAT-11563): ONE layout for every selection. Row 1 names the
    // language being read; row 2 is always the Voice slot on a dubbing surface —
    // the Original/Dubbed/Both toggle when the selected language has a dub, the
    // "Dub with AI Voice" button when it could get one, and a plain "Original"
    // otherwise — so the block never changes shape as the selection moves. The
    // music line and coverage notice follow only where a dub exists to govern.
    const headerCode = selectedLanguage ?? originalCode;
    const headerCaption =
      byCode.get(headerCode)?.caption ?? formatLanguageCaption(headerCode);
    const showVoiceToggle = !!onAudioModeChange && selectedHasDub;
    // The first-dub handler wins the header button when both are wired
    // (PLAT-11440); the button belongs to the language being READ.
    const dubButtonAction = onRequestFirstDub ?? onRequestDub;
    const headerDub =
      onAudioModeChange &&
      selectedLanguage &&
      !selectedHasDub &&
      dubButtonAction
        ? { language: selectedLanguage, run: dubButtonAction }
        : null;
    // PLAT-11685 — the toolbar microphone does whatever the selected language
    // is ready for: REVIEW the dub when there is one, otherwise START it,
    // opening the same modal as the menu's own "Dub with AI Voice" rather than
    // a Dubbing Review with nothing in it. Inert only where neither job exists
    // — on Original (no language named), over the duration cap (where the
    // note's own way-out sentence is the hint, PLAT-11589), or on a surface
    // that wires no dub action at all.
    const reviewDubStart =
      selectedLanguage && !selectedHasDub && !dubUnavailableNote
        ? dubButtonAction
        : undefined;
    const reviewDubTooltip = !selectedLanguage
      ? 'Select a translation to review the dub'
      : selectedHasDub
        ? 'Review Dub'
        : dubUnavailableNote
          ? dubUnavailableNote.tooltip
          : reviewDubStart
            ? 'Dub with AI Voice'
            : 'Dub with AI Voice first to review the dub';
    const reviewDubActive = selectedHasDub || !!reviewDubStart;
    // The list holds only the OTHER languages (PLAT-11563): the one being read
    // is represented by the tinted block (with its kebab) and hidden from the
    // rows via `hideSelectedRow`, so "Switch To" is literally what the rows
    // are. The heading rides the header node, which ItemDropdownSelect pins
    // above the scrolling list.
    // The reading language's Voice / Music / coverage controls: under the
    // dropdown's header row, and under the reading row of the Languages tab.
    const voiceControls = (
      <>
        {/* No Voice row on Original: nothing can be dubbed there, so the only
          value would be a third "Original" under two that already say it.
          With the pickers beside the player (PLAT-11877) the row keeps only
          the way to START a dub — the one thing the player can't offer. */}
        {onAudioModeChange &&
          selectedLanguage &&
          (showVoiceToggle ? (
            !audioControlsInPlayer && (
              <HeaderToggle
                mode={audioMode}
                onChange={onAudioModeChange}
                busy={audioModeBusy}
                tooltipId={LANGUAGE_MENU_TOOLTIP_ID}
              />
            )
          ) : audioControlsInPlayer && !headerDub ? null : (
            <VoiceSlotRow>
              {!audioControlsInPlayer && <span className="label">Voice</span>}
              {headerDub ? (
                <HeaderDubButton
                  type="button"
                  // aria-disabled, not disabled: a disabled button takes no
                  // pointer events, so the tooltip saying why would never
                  // show (PLAT-11589).
                  aria-disabled={dubUnavailableNote ? true : undefined}
                  data-tooltip-id={
                    dubUnavailableNote ? LANGUAGE_MENU_TOOLTIP_ID : undefined
                  }
                  data-tooltip-content={dubUnavailableNote?.tooltip}
                  onClick={() => {
                    if (dubUnavailableNote) return;
                    headerDub.run(headerDub.language);
                    closeDropdown();
                  }}
                >
                  <MicrophoneIcon
                    width="15"
                    height="15"
                    fillColor="currentColor"
                  />
                  Dub with AI Voice
                </HeaderDubButton>
              ) : (
                <span className="value">Original</span>
              )}
            </VoiceSlotRow>
          ))}
        {/* PLAT-11532 — why the Dub button above is inert, as a one-line
          warning (PLAT-11589); the way out is the button's tooltip. Only
          under a button that exists: with a dub already in place there is
          nothing to start, and on Original there is no Voice slot. */}
        {headerDub && dubUnavailableNote && (
          <DubUnavailableNote role="status">
            <CircleExclammationIcon
              width="13"
              height="13"
              strokeColor="currentColor"
              strokeWidth={1.75}
            />
            {dubUnavailableNote.line}
          </DubUnavailableNote>
        )}
        {/* PLAT-10622/PLAT-10664/PLAT-10628: the voice/music line and the
        coverage notice sit under the Voice toggle once the selected
        language has a dub — the same rule as the toggle, so they can only
        appear where a dub exists to be governed or incomplete. */}
        {/* PLAT-11899 — the dub's level (and on Both the original's), the
            one player setting that stays in this menu for now. */}
        {showVoiceToggle && dubLevelControl}
        {!audioControlsInPlayer && showVoiceToggle && musicControl}
        {showVoiceToggle &&
          (typeof coverageNotice === 'function'
            ? coverageNotice({ close: closeDropdown })
            : coverageNotice)}
      </>
    );
    const audioModeHeader = showHeader ? (
      <>
        <HeaderBlock data-context-block="">
          <SelectedVoiceRow>
            <SelectedVoiceMain>
              <SelectedVoiceLabel>{headerCaption}</SelectedVoiceLabel>
              <SelectedVoiceCode>
                {normalizeLangKey(headerCode)}
              </SelectedVoiceCode>
              {!selectedLanguage && <OriginalNote>Original</OriginalNote>}
            </SelectedVoiceMain>
            <SelectedVoiceRight>
              {/* The language being read is not repeated in the list below, so
                  its actions live here — the same pencil / mic the rows carry
                  (PLAT-11662) and the one kebab it gets. Original has nothing
                  to act on. */}
              {selectedLanguage && (
                <RowReviewActions
                  onReviewTranslation={reviewTranslationFor(selectedLanguage)}
                  onReviewDub={reviewDubFor(selectedLanguage, selectedHasDub)}
                  labelled={inPanel}
                />
              )}
              {selectedLanguage &&
                rowActionsFor(selectedLanguage, headerCaption, selectedHasDub)}
            </SelectedVoiceRight>
          </SelectedVoiceRow>
          {voiceControls}
        </HeaderBlock>
        <ListHeading>Switch To</ListHeading>
      </>
    ) : undefined;

    // Trigger tooltip (PLAT-11563): the pill shows only the compact code, so
    // the tooltip expands it to the full name and, for a translation on a
    // dubbing surface, says which voice is playing. The source language gets
    // no voice part — nothing can be dubbed there — and is marked as the
    // original so "original" never has to double as an audio word.
    const voiceLabel =
      !onAudioModeChange || !selectedLanguage
        ? null
        : showVoiceToggle
          ? AUDIO_MODE_LABELS[audioMode]
          : AUDIO_MODE_LABELS.original;
    // A dubbed language playing its ORIGINAL voice shows the pill mic beside a
    // tooltip that would otherwise only say "Original voice" — name the dub.
    const dubAvailable =
      selectedHasDub && voiceLabel === AUDIO_MODE_LABELS.original
        ? ' · Dub available'
        : '';
    const triggerTooltip = !selectedLanguage
      ? `Reading in ${headerCaption} (original language)`
      : voiceLabel
        ? `Reading in ${headerCaption} · ${voiceLabel}${dubAvailable}`
        : `Reading in ${headerCaption}`;

    const addLanguageFooter =
      hasUnfilledLanguages && !generateDisabled
        ? (close: () => void) => (
            <AddLanguageFooter>
              <HeaderDubButton
                type="button"
                aria-disabled={generateInertNote ? true : undefined}
                data-tooltip-id={
                  generateInertNote ? LANGUAGE_MENU_TOOLTIP_ID : undefined
                }
                data-tooltip-content={generateInertNote}
                onClick={
                  generateInertNote
                    ? undefined
                    : () => {
                        onRequestGenerate();
                        close();
                      }
                }
              >
                {/* 40-unit viewBox drawn at 14px: strokeWidth 4 renders ~1.4px,
                    matching the mic glyph beside "Dub with AI Voice". */}
                <PlusIcon
                  width="14"
                  height="14"
                  strokeColor="currentColor"
                  strokeWidth={4}
                />
                Add Translation
              </HeaderDubButton>
            </AddLanguageFooter>
          )
        : undefined;

    // The remove confirm and the dub-info popover belong to whichever
    // presentation opened them (the dropdown's rows, the panel's, or the ⋯
    // menu's through the toolbar button).
    const actionOverlays = (
      <>
        {/* PLAT-10732: the kebab's "Dub info" popover, LIFTED here so closing the
            language dropdown (which unmounts the kebab) doesn't take it with it.
            Fixed at the coordinates the kebab captured; content is the same
            `dubInfoTooltipContent` mapping the hover overlay uses. */}
        {dubInfoTarget &&
          (() => {
            const { entries, titleValue, section, sections } =
              dubInfoTooltipContent(dubInfo?.[dubInfoTarget.code]);
            return createPortal(
              <DubInfoPopover
                ref={dubInfoRef}
                onMouseDown={e => e.stopPropagation()}
                style={{
                  top: dubInfoTarget.y,
                  left: dubInfoTarget.x,
                  zIndex: CLIP_PREVIEW_Z.languageDubMicTooltip,
                }}
              >
                <ClipContextTooltip
                  title="Dub info"
                  titleValue={titleValue}
                  entries={entries}
                  section={section}
                  sections={sections}
                />
              </DubInfoPopover>,
              document.body,
            );
          })()}
        {confirmRemoveLang && (
          <ConfirmModal
            closeModal={() => setConfirmRemoveLang(null)}
            onCancel={() => setConfirmRemoveLang(null)}
            onConfirm={() => {
              const lang = confirmRemoveLang;
              setConfirmRemoveLang(null);
              void runRemove(lang);
            }}
            title={`Remove ${
              byCode.get(confirmRemoveLang)?.caption ?? confirmRemoveLang
            }?`}
            message={
              dubbedLanguages?.includes(confirmRemoveLang) &&
              onRemoveDubForLanguage
                ? 'The translation and its dub will be removed from this story.'
                : 'The translation will be removed from this story.'
            }
            confirmText="Remove Language"
            cancelText="Cancel"
          />
        )}
      </>
    );

    const sharedOverlays = (
      <>
        {/* PLAT-10732: one shared dub-info overlay for every green mic. Anchored
            by `data-tooltip-id`; the hovered mic's `data-dub-lang` selects which
            language's cached rows to render. Portaled to <body> so it isn't
            clipped by the transcript column's overflow (it can extend over the
            Speaker view on the right). */}
        {createPortal(
          <Tooltip
            id={DUB_MIC_TOOLTIP_ID}
            // Prefer the RIGHT of the language dropdown so it doesn't cover the
            // menu rows; `DUB_MIC_MIDDLEWARES` flips it left only when the right
            // has no room, so it is always fully visible (PLAT-11563).
            place="right-start"
            middlewares={DUB_MIC_MIDDLEWARES}
            // Don't close the overlay when the wheel scrolls — the user scrolls
            // INSIDE it to read a long collapsible list. `clickOutsideAnchor` is
            // left off so clicking the caret (outside the mic anchor) doesn't
            // dismiss it (PLAT-10732).
            globalCloseEvents={{ escape: true, scroll: false, resize: false }}
            // Keep the overlay open when the pointer moves onto it, so the
            // collapsible caret is clickable (PLAT-10732).
            clickable
            // Grace period so the pointer can cross the `offset(10)` gap from the
            // mic onto the overlay without it dismissing. Without a delay the mic
            // `mouseleave` hides it before the pointer lands on the (clickable)
            // overlay, so grabbing the scrollbar/wheel is impossible (PLAT-10732).
            delayHide={250}
            // The mic anchor lives inside the language dropdown menu
            // (`languageDropdownMenu` z), and both this tooltip and the menu
            // portal to <body>. `DARK_TOOLTIP_STYLE`'s default z (100005) sits
            // far below the menu, so lift it one step above via the unified
            // clip-preview scale (PLAT-10732). See clipPreviewZ.ts.
            style={{
              ...DARK_TOOLTIP_STYLE,
              zIndex: CLIP_PREVIEW_Z.languageDubMicTooltip,
            }}
            opacity={1}
            render={({ activeAnchor }) => {
              const lang = activeAnchor?.getAttribute('data-dub-lang') ?? '';
              const { entries, titleValue, section, sections } =
                dubInfoTooltipContent(lang ? dubInfo?.[lang] : undefined);
              return (
                <DubOverlayBody>
                  <ClipContextTooltip
                    title="Has a dub"
                    titleValue={titleValue}
                    entries={entries}
                    section={section}
                    sections={sections}
                  />
                </DubOverlayBody>
              );
            }}
          />,
          document.body,
        )}
        {actionOverlays}
      </>
    );

    // PLAT-11877 — the toolbar's one language control: a globe and the count
    // of languages (the original plus its translations). It opens the host's
    // Languages panel; the runs in progress stay beside it, visible.
    if (presentation === 'button') {
      return (
        <>
          {languagesButton}
          {actionOverlays}
        </>
      );
    }

    // PLAT-11877 — the translation being read, named as the pane's title,
    // with the job it is there for (Review) and the way back to the original.
    if (presentation === 'title') {
      if (!selectedLanguage || !readingCaption) return null;
      // What a reader should know before pressing Review: the line only
      // exists on a translation, so it says the rest — a dub, and staleness.
      const readingStale = isTranslationStale(
        translationStore.translations.get(selectedLanguage),
      );
      const status = [
        selectedHasDub ? 'Dubbed' : null,
        readingStale ? 'Needs Update' : null,
      ]
        .filter(Boolean)
        .join(' · ');
      return (
        <TitleLine data-testid="translation-title-line">
          {/* Secondary to the video: the reader came to play it, so the line
              is one quiet row — an arrow, the name, and Review as a ghost —
              not a bar. The arrow says where it goes in its tooltip. */}
          <TitleBack
            type="button"
            aria-label="Back to Original Transcript"
            data-tooltip-id="translation-title-tooltip"
            data-tooltip-content="Back to Original Transcript"
            onClick={() => changeLanguage(null)}
          >
            <BackIcon
              width="14"
              height="10"
              strokeColor="currentColor"
              strokeWidth="2"
            />
          </TitleBack>
          <TitleText>
            <TitleName>{readingCaption}</TitleName>
            {status && (
              <TitleStatus $stale={readingStale}>{status}</TitleStatus>
            )}
          </TitleText>
          {showReview && onRequestReview && (
            <TitleReviewButton
              type="button"
              aria-label="Review Translation"
              data-testid="review-translation-button"
              onClick={() => onRequestReview(selectedLanguage, 'translation')}
            >
              <EditPenIcon size={13} />
              Review Translation
            </TitleReviewButton>
          )}
          {ReactDOM.createPortal(
            <Tooltip
              id="translation-title-tooltip"
              style={COMPACT_TOOLTIP_STYLE}
              place="bottom"
              positionStrategy="fixed"
            />,
            document.body,
          )}
        </TitleLine>
      );
    }

    // PLAT-11877 — the Languages list, filling the transcript pane: the same
    // header block the dropdown pins (the language being read, its Voice and
    // Music) and the same rows, each with its actions, then Add Translation.
    // Picking a row reads that language and returns to the transcript.
    if (inPanel) {
      // The dropdown's own anatomy (design-system.md "Rich Dropdown Menu"),
      // given the pane: the context block for the language being read (its
      // Review, its ⋯, its Voice and Music), "Switch To", the same rows, and
      // Add Translation. Picking a row reads it and returns to the transcript.
      const readingValue = selectedLanguage ?? ORIGINAL_VALUE;
      const pick = (value: string | null) => {
        changeLanguage(value === ORIGINAL_VALUE ? null : value);
        onClosePanel?.();
      };
      return (
        <>
          <TranslationMenuGlobalStyle />
          <LanguagesPanelGlobalStyle />
          <LanguagesPanel data-testid="languages-panel">
            {panelBack && languagesPanelHead}
            {languagesPanelRun}
            <LanguagesPanelList
              className={`${LANGUAGE_MENU_CLASS} ${LANGUAGES_PANEL_CLASS}`}
              role="listbox"
              aria-label="Languages"
            >
              <ListHeading>Current Language</ListHeading>
              {audioModeHeader}
              {values
                .filter(v => v.value !== readingValue)
                .map(v => (
                  <LanguagesPanelRow
                    key={String(v.value)}
                    role="option"
                    aria-selected={false}
                    tabIndex={0}
                    data-testid={`languages-panel-row-${v.value}`}
                    onClick={() => pick(v.value)}
                    onKeyDown={event => {
                      // Keys from the row's kebab (and its portaled menu)
                      // bubble here; only the row itself picks.
                      if (event.target !== event.currentTarget) return;
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        pick(v.value);
                      }
                    }}
                  >
                    {v.caption}
                  </LanguagesPanelRow>
                ))}
              {addLanguageFooter?.(() => onClosePanel?.())}
            </LanguagesPanelList>
          </LanguagesPanel>
          {ReactDOM.createPortal(
            <Tooltip
              id={LANGUAGE_MENU_TOOLTIP_ID}
              style={COMPACT_TOOLTIP_STYLE}
              place="top"
              positionStrategy="fixed"
            />,
            document.body,
          )}
          {ReactDOM.createPortal(
            <Tooltip
              id={LANGUAGE_MENU_HELP_TOOLTIP_ID}
              style={PARAGRAPH_TOOLTIP_STYLE}
              place="top"
              positionStrategy="fixed"
            />,
            document.body,
          )}
          {ReactDOM.createPortal(
            <Tooltip
              id="translation-stale-tooltip"
              style={PARAGRAPH_TOOLTIP_STYLE}
              place="bottom"
              positionStrategy="fixed"
            />,
            document.body,
          )}
          {sharedOverlays}
        </>
      );
    }

    return (
      <>
        <TranslationMenuGlobalStyle />
        <ToggleWrapper
          aria-label="View transcript in language"
          role="group"
          ref={scrollRef}
        >
          <TriggerAnchor
            data-tooltip-id="translation-toggle-trigger-tooltip"
            data-tooltip-content={menuOpen ? undefined : triggerTooltip}
          >
            <ItemDropdownSelect
              ref={dropdownRef}
              onOpenChange={setMenuOpen}
              // With the header on, the tinted block IS the current language;
              // the rows are the ones to switch to (PLAT-11563). Without it
              // (captions toolbar) the list keeps showing the selection.
              hideSelectedRow={showHeader}
              propertyName="viewLanguage"
              menuClassName={LANGUAGE_MENU_CLASS}
              menuHeader={audioModeHeader}
              menuFooter={addLanguageFooter}
              // +20% over the prior 260 cap so more languages show before the
              // list scrolls between the pinned header and Add-language footer;
              // the edge fade cues the rest (PLAT-10775).
              // PLAT-11563: the cap bounds the WHOLE panel, and the header block
              // (language, Voice, music line, hint) takes ~250px of it, so the old
              // 312 left three rows and made the rest a scroll hunt. 560 shows the
              // header plus ~8 rows; useAnchoredDropdown still clamps to the
              // space below the trigger on a short window.
              maxMenuHeight={LANGUAGE_MENU_MAX_HEIGHT}
              defaultValue={selectedLanguage ?? ORIGINAL_VALUE}
              values={values}
              action={(_, value) => {
                if (value === ORIGINAL_VALUE || value === null) {
                  changeLanguage(null);
                  return;
                }
                changeLanguage(value);
              }}
            />
          </TriggerAnchor>
          {/* PLAT-10444 / PLAT-11662: standalone open buttons beside the
              dropdown, so the review is reachable without opening the menu —
              one per JOB, with the review's own tab glyphs: the pencil opens
              Translation Review (the globe stays the language chip's), the
              microphone Dubbing Review. The pencil renders whenever the flag is on and the parent hosts the
              workspace, merely DISABLED while Original is selected
              (PLAT-10646 — a stable toolbar beats an appearing button); the
              microphone only for a language that has a dub. */}
          {enableTranslationReview && onRequestReview && (
            <ReviewButton
              type="button"
              aria-label="Review Translation"
              // aria-disabled, not native disabled: a disabled button takes no
              // pointer events, so the "Select a translation to review" hint
              // below could never show (PLAT-11603). The click is guarded here.
              aria-disabled={selectedLanguage ? undefined : true}
              onClick={() => {
                if (selectedLanguage) {
                  onRequestReview(selectedLanguage, 'translation');
                }
              }}
              data-testid="review-translation-button"
              data-tooltip-id="translation-review-tooltip"
              data-tooltip-content={
                selectedLanguage
                  ? 'Review Translation'
                  : 'Select a translation to review'
              }
            >
              <TranslateIcon aria-hidden="true">
                <EditPenIcon size={14} />
              </TranslateIcon>
            </ReviewButton>
          )}
          {enableTranslationReview && onRequestReview && (
            <ReviewButton
              type="button"
              // The accessible name follows the job, as the tooltip does: a
              // reader who cannot see which state the mic is in would otherwise
              // be told it reviews a dub that does not exist yet.
              aria-label={reviewDubStart ? 'Dub with AI Voice' : 'Review Dub'}
              // Always present so the toolbar never reflows. PLAT-11685: live
              // for any selected translation, dubbed or NOT — the old "has a
              // dub" gate greyed out the button that names the job. What it
              // DOES splits on the dub: review it, or start it. See
              // `reviewDubStart` above for the inert cases.
              aria-disabled={reviewDubActive ? undefined : true}
              onClick={() => {
                if (!selectedLanguage) return;
                if (selectedHasDub) {
                  onRequestReview(selectedLanguage, 'dubbing');
                  return;
                }
                reviewDubStart?.(selectedLanguage);
              }}
              data-testid="review-dub-button"
              data-tooltip-id="translation-review-tooltip"
              data-tooltip-content={reviewDubTooltip}
            >
              <TranslateIcon aria-hidden="true">
                <MicStrokeIcon size={14} />
              </TranslateIcon>
            </ReviewButton>
          )}
          {/* Hover hints for the header controls (Voice / Music segments, music
              kebab) and the Separate explanation — lifted above the menu like
              the stale tooltip so they show inside the clip preview too. */}
          {ReactDOM.createPortal(
            <Tooltip
              id={LANGUAGE_MENU_TOOLTIP_ID}
              style={{
                ...COMPACT_TOOLTIP_STYLE,
                zIndex: CLIP_PREVIEW_Z.languageDropdownMenu + 10,
              }}
              // Above the control: below, the hint lands on the next header row and
              // its gold segments fight the tooltip for contrast (PLAT-11563).
              place="top"
              positionStrategy="fixed"
            />,
            document.body,
          )}
          {ReactDOM.createPortal(
            <Tooltip
              id={LANGUAGE_MENU_HELP_TOOLTIP_ID}
              style={{
                ...PARAGRAPH_TOOLTIP_STYLE,
                zIndex: CLIP_PREVIEW_Z.languageDropdownMenu + 10,
              }}
              // Above the control: below, the hint lands on the next header row and
              // its gold segments fight the tooltip for contrast (PLAT-11563).
              place="top"
              positionStrategy="fixed"
            />,
            document.body,
          )}
          {ReactDOM.createPortal(
            <Tooltip
              id="translation-toggle-trigger-tooltip"
              style={TRANSLATION_TOOLTIP_STYLE}
              place="bottom"
              positionStrategy="fixed"
            />,
            document.body,
          )}
          {ReactDOM.createPortal(
            <Tooltip
              id="translation-review-tooltip"
              style={COMPACT_TOOLTIP_STYLE}
              place="bottom"
              positionStrategy="fixed"
            />,
            document.body,
          )}
          {/* Anchored to the stale-translation warning icon inside the
              portaled language menu. The menu sits at
              `languageDropdownMenu` z (PLAT-10216); lift the tooltip above it
              so it isn't clipped behind the menu inside the clip preview. */}
          {ReactDOM.createPortal(
            <Tooltip
              id="translation-stale-tooltip"
              // Two sentences of explanation, so the wrapping paragraph token,
              // not the single-line compact one (PLAT-11563).
              style={{
                ...PARAGRAPH_TOOLTIP_STYLE,
                zIndex: CLIP_PREVIEW_Z.languageDropdownMenu + 10,
              }}
              place="bottom"
              positionStrategy="fixed"
            />,
            document.body,
          )}
        </ToggleWrapper>
        {/* PLAT-11570: a dub generating in the background keeps a chip beside
            the toggle (not in place of it — a dub runs for minutes and the
            language must stay switchable meanwhile) until the run lands.
            PLAT-11690 put translation under the same rule, so both can be here
            at once — they are independent runs. `RunChipRow`'s `min-width: 0`
            is what lets their codes ellipsize rather than the pair overrunning
            the pane and being cut; the review window's transcript column
            leaves ~145px here. */}
        <RunChipRow>
          <DubRunChip />
          <TranslationRunChip />
        </RunChipRow>
        {sharedOverlays}
      </>
    );
  },
);

// Rough menu / popover sizes for viewport clamping (the popover self-sizes;
// these are just the clamp bounds so it never spills off-screen).
const ROW_MENU_WIDTH = 210;
const ROW_MENU_ROW = 36;
const DUB_INFO_WIDTH = 300;
const DUB_INFO_HEIGHT = 340;

/**
 * PLAT-11563 — the per-language row kebab: every action on a translated
 * language in one menu, scoped to the row it sits on (not to the selection).
 * Dub entries first (Dub with AI Voice / Re-dub, Dub Info), then the
 * translation ones (Review, Regenerate), then the destructive pair. Each entry
 * is present only when its callback is — the parent applies the surface gates.
 *
 * The kebab lives inside an `ItemDropdownSelect` row, so both it and the
 * portaled menu swallow `click` (a React portal bubbles synthetic events to
 * the row, which would select the language) and `mousedown` (the dropdown's
 * `useAnchoredDropdown` closes on any document mousedown outside the menu, and
 * this menu portals outside it).
 */
const LanguageRowActionsMenu: React.FC<{
  /** Language caption, for the kebab's accessible name. */
  label: string;
  /** Picks the dub entry's wording: "Re-dub" over an existing dub. */
  hasDub: boolean;
  onDub?: () => void;
  /** PLAT-11532 — the video is over the dubbing cap, so the dub entry stays
   * listed (the action exists) but cannot be pressed; this sentence is its
   * hover tooltip saying why (PLAT-11589). */
  dubDisabledNote?: string;
  /** Show the dub-info popover at the given viewport coords; the parent renders
   * it (lifted so it survives the dropdown closing — PLAT-10732). */
  onDubInfo?: (pos: { x: number; y: number }) => void;
  onReviewTranslation?: () => void;
  /** PLAT-11662 — present only for a language that has a dub. */
  onReviewDub?: () => void;
  onRegenerate?: () => void;
  /** PLAT-11690 — a translation is already running, so Regenerate stays listed
   * but cannot be pressed; this sentence is its hover tooltip saying why. */
  regenerateDisabledNote?: string;
  onRemoveDub?: () => void;
  onRemove: () => void;
}> = ({
  label,
  hasDub,
  onDub,
  dubDisabledNote,
  onDubInfo,
  onReviewTranslation,
  onReviewDub,
  onRegenerate,
  regenerateDisabledNote,
  onRemoveDub,
  onRemove,
}) => {
  const kebabRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState<{ x: number; y: number } | null>(null);

  const closeMenu = useCallback(() => setMenuPos(null), []);
  // Ignore outside-clicks that land on the kebab itself — its own onClick
  // toggles the menu, so letting the outside handler also fire would race it.
  const onOutsideMenu = useCallback(
    (e: MouseEvent) => {
      const target = e.target;
      if (target instanceof Node && kebabRef.current?.contains(target)) return;
      closeMenu();
    },
    [closeMenu],
  );
  useOutsideAlerter(menuRef, onOutsideMenu);

  useEffect(() => {
    if (!menuPos) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMenu();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuPos, closeMenu]);

  const anchorBelow = (width: number, height: number) => {
    const rect = kebabRef.current?.getBoundingClientRect();
    if (!rect) return null;
    return clampMenuToViewport(
      rect.right - width,
      rect.bottom + 4,
      width,
      height,
    );
  };

  const rows =
    1 +
    (onDub ? 1 : 0) +
    (onDubInfo ? 1 : 0) +
    (onReviewTranslation ? 1 : 0) +
    (onReviewDub ? 1 : 0) +
    (onRegenerate ? 1 : 0) +
    (onRemoveDub ? 1 : 0);

  const toggleMenu = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (menuPos) {
      closeMenu();
      return;
    }
    setMenuPos(anchorBelow(ROW_MENU_WIDTH, rows * ROW_MENU_ROW + 12));
  };

  // Close the menu, then run the parent action (which also closes the language
  // dropdown this kebab lives in).
  const run = (fn: () => void) => () => {
    closeMenu();
    fn();
  };

  // Capture the popover position while the kebab is still mounted, then hand
  // off to the parent (which shows the lifted popover + closes the dropdown).
  const showInfo = () => {
    const pos = anchorBelow(DUB_INFO_WIDTH, DUB_INFO_HEIGHT);
    closeMenu();
    if (pos) onDubInfo?.(pos);
  };

  return (
    <>
      <RowKebab
        ref={kebabRef}
        type="button"
        aria-label={`Actions for ${label}`}
        aria-haspopup="menu"
        aria-expanded={!!menuPos}
        data-row-kebab=""
        onClick={toggleMenu}
      >
        <MoreVertIcon />
      </RowKebab>
      {menuPos &&
        createPortal(
          <ContextMenuSurface
            ref={menuRef}
            role="menu"
            onMouseDown={e => e.stopPropagation()}
            onClick={e => e.stopPropagation()}
            style={{
              top: menuPos.y,
              left: menuPos.x,
              zIndex: CLIP_PREVIEW_Z.languageDubMicTooltip,
            }}
          >
            {/* Ordered by action, translation before dub within each
                (PLAT-11662): review, review · regenerate, regenerate ·
                remove, remove. Dub Info rides with the dub's regenerate. */}
            {onReviewTranslation && (
              <ContextMenuItem
                role="menuitem"
                onClick={run(onReviewTranslation)}
              >
                <EditPenIcon size={15} /> Review Translation
              </ContextMenuItem>
            )}
            {onReviewDub && (
              <ContextMenuItem role="menuitem" onClick={run(onReviewDub)}>
                <MicStrokeIcon size={15} /> Review Dub
              </ContextMenuItem>
            )}
            {onRegenerate && (
              <ContextMenuItem
                role="menuitem"
                onClick={regenerateDisabledNote ? undefined : run(onRegenerate)}
                aria-disabled={regenerateDisabledNote ? true : undefined}
                $disabled={!!regenerateDisabledNote}
                data-tooltip-id={
                  regenerateDisabledNote ? LANGUAGE_MENU_TOOLTIP_ID : undefined
                }
                data-tooltip-content={regenerateDisabledNote}
              >
                <RefreshIcon
                  width="15"
                  height="15"
                  strokeColor="currentColor"
                />
                Regenerate Translation
              </ContextMenuItem>
            )}
            {onDub && (
              <ContextMenuItem
                role="menuitem"
                onClick={dubDisabledNote ? undefined : run(onDub)}
                aria-disabled={dubDisabledNote ? true : undefined}
                $disabled={!!dubDisabledNote}
                data-tooltip-id={
                  dubDisabledNote ? LANGUAGE_MENU_TOOLTIP_ID : undefined
                }
                data-tooltip-content={dubDisabledNote}
              >
                <MicrophoneIcon
                  width="15"
                  height="15"
                  fillColor="currentColor"
                />
                {hasDub ? 'Regenerate Dub' : 'Dub with AI Voice'}
              </ContextMenuItem>
            )}
            {onDubInfo && (
              <ContextMenuItem role="menuitem" onClick={showInfo}>
                <InfoIcon /> Dub Info
              </ContextMenuItem>
            )}
            <ContextMenuItem role="menuitem" $danger onClick={run(onRemove)}>
              <DeleteIcon width="15" height="15" /> Remove Language
            </ContextMenuItem>
            {onRemoveDub && (
              <ContextMenuItem
                role="menuitem"
                $danger
                onClick={run(onRemoveDub)}
              >
                <DeleteIcon width="15" height="15" />
                Remove Dub
              </ContextMenuItem>
            )}
          </ContextMenuSurface>,
          document.body,
        )}
    </>
  );
};

// The row kebab: the shared trigger, squared to the dense rows and always
// visible at rest in --text-muted, like every other row kebab in the app (the
// Highlights dropdown beside this menu, Story Manager, Speaker Manager) —
// design-system.md "Context / Action Menus". Same overrides as
// SavedSegmentsFilterDropdown's CombineRowButton.
const RowKebab = styled(KebabButton)`
  width: 20px;
  height: 20px;
  padding: 2px;
  margin-left: 0;
  flex-shrink: 0;
  align-self: center;
`;

// Holds the kebab's width on the Original row so every row's code column lines
// up whether or not the row has actions.
const RowActionsSpacer = styled.span`
  display: inline-block;
  width: 20px;
  flex-shrink: 0;
`;

// Positioned wrapper for the click-opened dub-info popover. ClipContextTooltip
// carries its own surface styling (background/border/shadow/width), so this only
// pins it to a fixed viewport position (PLAT-10732).
const DubInfoPopover = styled.div`
  position: fixed;
`;

// The header block (PLAT-11563): one bordered group holding the selected
// language row, the Voice slot and (when a dub exists) the music line and
// coverage notice. It owns the single divider to the list below and strips the
// dividers/margins its children carry for other hosts, so the block reads as
// one section however many rows it has.
const HeaderBlock = styled.div`
  /* One depth step above the panel (design-system.md "Pinned context header"):
     the block reads as the CURRENT state, the rows below as the options. The
     panel itself is on --surface-subtle, so this is --surface. Bleeds over the
     panel's 4px padding to its edges and takes its top radius; the padding is
     added back so the rows' content does not move. Row hover stays on
     --surface-elevated, one step brighter than this tint. */
  background: var(--surface);
  margin: -4px -4px 4px;
  padding: 4px 4px 0;
  border-radius: 8px 8px 0 0;
  border-bottom: 1px solid var(--border);

  && > * {
    border-bottom: none;
    margin-bottom: 0;
  }
`;

// Selected-language header row: the title (plus the Original badge) with the
// muted code right-aligned.
const SelectedVoiceRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 8px 8px;
`;

// Right cluster of the header row: the muted code plus the current language's
// kebab, aligned like a row's code + kebab.
const SelectedVoiceRight = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const SelectedVoiceMain = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

// Heading pinned over the scrolling language rows, on the rows' 10px inset. A
// section heading, not a field label: one step brighter than the header's
// "Voice" label (--text-secondary vs --text-muted) with more air above, so the
// two don't read as the same kind of text. No divider of its own — the tinted
// block's bottom border already separates the regions.
const ListHeading = styled.div`
  /* Sits at a smaller inset than the rows (6px vs their 14px), so the rows read
     as nested under it — separation by structure, not by a divider. 8px above
     (plus the header block's 4px) keeps it close to the divider (PLAT-11563). */
  padding: 8px 6px 2px;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--text-secondary);
`;

// The panel's title: brighter and a step larger than the rows below it, so
// it leads the block instead of reading dimmer than the list (PLAT-11563).
const SelectedVoiceLabel = styled.span`
  font-size: var(--text-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--foreground);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// "Original" as the design system's note tag (the Split Button's "Default"):
// plain muted text beside the label, no chrome — it qualifies the row rather
// than flagging a state. Shared by the header and the Original row.
const OriginalNote = styled.span`
  flex-shrink: 0;
  color: var(--text-muted);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  line-height: 1.2;
`;

// The code as plain muted text, the same treatment as the row codes below. It
// used to be a gold pill, but gold already marks the selected row, and a second
// gold element made the header compete with the list (PLAT-11563).
const SelectedVoiceCode = styled.span`
  flex-shrink: 0;
  color: var(--text-muted);
  font-size: var(--text-xs);
  line-height: 1.2;
  font-weight: var(--font-weight-medium);
  letter-spacing: 0.02em;
  font-variant-numeric: tabular-nums;
`;

// The Voice slot when the selected language has no dub to toggle (PLAT-11563):
// same label + right-aligned control shape as the segmented toggle, holding
// either the "Dub with AI Voice" button or a plain "Original", so the header
// keeps its shape across selections.
const VoiceSlotRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 8px 8px;

  .label {
    font-size: var(--text-xs);
    font-weight: var(--font-weight-semibold);
    color: var(--text-muted);
  }
  .value {
    padding: 3px 10px;
    font-size: var(--text-xs);
    font-weight: var(--font-weight-medium);
    color: var(--text-secondary);
  }
`;

// Pinned footer holding the list's one creation action. Left-aligned on the
// rows' 14px inset so it reads as the list's own "add" row; it wears the same
// outline chrome as "Dub with AI Voice" so the two "start something" actions
// read as peers (PLAT-11563, ticket item 12). No top divider.
const AddLanguageFooter = styled.div`
  display: flex;
  justify-content: flex-start;
  padding: 6px 8px 8px 14px;
`;

// The menu's outlined action button: "Dub with AI Voice" in the Voice slot when
// no dub exists yet, and "Add Translation" in the footer — sized like the
// segmented toggle (icon + label).
const HeaderDubButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 3px 10px;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: var(--foreground);
  background: transparent;
  border: 1px solid var(--border);
  border-radius: 6px;
  cursor: pointer;
  transition:
    background-color 120ms ease,
    color 120ms ease;

  &:hover:not(:disabled):not([aria-disabled='true']) {
    background: var(--surface-elevated);
    color: var(--foreground);
  }

  &:disabled,
  &[aria-disabled='true'] {
    cursor: default;
    color: var(--text-muted);
  }
`;

// PLAT-11532 / PLAT-11589 — the one-line warning under an inert Dub button.
// Gold (--accent) with a glyph so it scans as "worth reading, not a fault" (the
// same tone DubCoverageNotice uses for an informative notice), one line so the
// header does not grow; inset to the Voice slot's padding so it hangs under the
// button it explains. The way out lives in the button's tooltip, not here.
const DubUnavailableNote = styled.p`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  padding: 0 8px 8px;
  white-space: nowrap;
  font-size: var(--text-xs);
  color: var(--accent);

  svg {
    flex: none;
  }
`;

// The shared Voice toggle, re-padded to match `VoiceSlotRow` so the Voice slot
// is the same size whichever control fills it. `&&` beats the base component's
// single-class specificity; `HeaderBlock` strips its divider.
const HeaderToggle = styled(AudioModeSegmentedToggle)`
  && {
    padding: 6px 8px 8px;
    margin-bottom: 0;
    border-bottom: none;
  }
`;

// Tooltip anchor around the dropdown only — not the whole ToggleWrapper, whose
// Review button carries its own tooltip and would otherwise show both.
const TriggerAnchor = styled.span`
  display: inline-flex;
  align-items: center;
  height: 100%;
`;

const ToggleWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  height: 100%;
  color: var(--text-secondary);

  /* Match sibling PanelButton styling inside the transcript header row. */
  .Selected {
    border: none;
    background-color: transparent;
    color: var(--text-secondary);
    padding: 4px 6px;
    height: 100%;
    border-radius: var(--radius-button);
    font-size: var(--text-xs);
    transition: all 150ms ease-out;
  }

  .Selected:hover {
    background-color: var(--surface-elevated);
    color: var(--foreground);
  }

  /* Center the trigger's inline children on the flex centerline. The chip
     span and the chevron button otherwise sit on the text BASELINE
     (default inline layout), dropping the globe/code/chevron a couple of
     pixels below the row's other (flex-centered) icon buttons. */
  .Selected > span,
  .Selected > button {
    display: inline-flex;
    align-items: center;
    padding: 0;
    line-height: 1;
  }
  .Selected svg {
    display: block;
  }

  /* Inside the trigger, only show the chip (globe + compact code) so the
     toolbar control stays compact. The two-column menu row is revealed by
     \`TranslationMenuGlobalStyle\` below — the menu portals to <body> via
     \`ItemDropdownSelect\`/\`useAnchoredDropdown\`, so it can't be reached
     from this wrapper-scoped rule and is styled by class instead. */
  .Selected [data-option-row] {
    display: none;
  }
`;

/**
 * Class applied to the portaled menu panel so the dual-shape language
 * entries render their full-name row inside the menu. Kept stable (not a
 * styled-component hash) because the menu lives in a body-level portal and
 * \`createGlobalStyle\` targets it by this literal class name.
 */
const LANGUAGE_MENU_CLASS = 'translation-language-menu';

/** Panel height cap — header block plus about eight language rows. */
const LANGUAGE_MENU_MAX_HEIGHT = 560;

/* Menu entries show the two-column row (full language name left, BCP 47
   code right-aligned). The trigger collapses to just the chip (see
   \`ToggleWrapper\`). \`ItemDropdownSelect\` portals the menu to <body> and
   sizes it to the trigger's (compact) width via an inline style, so we
   override the width with \`!important\` and grow to \`max-content\` to fit
   the longest caption (e.g. "Chinese (Simplified, Mainland China)") up to
   90vw. The hook (\`useAnchoredDropdown\`) owns position/z-index. */
const TranslationMenuGlobalStyle = createGlobalStyle`
  .${LANGUAGE_MENU_CLASS} {
    /* One fixed width, whatever is inside. It used to be max-content, which
       asks for the width the longest line wants without wrapping: a
       sentence-length notice under the header first stretched the menu off the
       side of the clip preview, and then, once capped, made it jump between
       two widths as those notices came and went. A constant width holds the
       header rows still. 440px is sized to the widest header state — the
       "Music and Sounds" line with its None / From Original segments and the
       music kebab — and still fits the longest language caption ("Chinese
       (Simplified, Mainland China)") beside a mic, a "Needs Update" badge and
       a code on one row (PLAT-11563; was 520). The 90vw is the floor for a
       narrow window. */
    width: min(440px, 90vw) !important;
    min-width: 320px !important;
    /* One depth step above the page (PLAT-11563): the panel used to sit on
       --background, the app's own background, and blended into it. The header
       block sits one step above this again; row hover stays --surface-elevated.
       ItemDropdownSelect reads this variable for the panel and its edge fades. */
    --menu-surface: var(--surface-subtle);
    white-space: nowrap;
    /* useAnchoredDropdown assigns a ~100k z-index, which sits below the
       clip-preview modal stack (~1,000,000) — so inside the clip preview
       the menu rendered behind the modal. Lift it above that stack.
       Harmless in Creator Studio. See clipPreviewZ.ts (PLAT-10216). */
    z-index: ${CLIP_PREVIEW_Z.languageDropdownMenu} !important;
  }

  /* Inside dropdown options, only show the row (chip becomes hidden). */
  .${LANGUAGE_MENU_CLASS} [data-option-chip] {
    display: none;
  }
  .${LANGUAGE_MENU_CLASS} [data-option-row] {
    display: grid;
  }

  /* Each dropdown option (DropdownItem) is a flex item of the Dropdown
     column-flex. Stretch them to full width and make the wrapper's flex
     container fill that width too so the grid row has real horizontal
     space to place the code on the right. Dense rows on the --text-xs token
     (~28px tall) — ItemDropdownSelect's stock 32px/14px rows read oversized
     next to the compact header (PLAT-10646). PLAT-11563 moved the rows from a
     hardcoded 11px onto the token so the notes, badge and heading that
     annotate them are the same size as the text they qualify. */
  .${LANGUAGE_MENU_CLASS} > *,
  .${LANGUAGE_MENU_CLASS} [data-dropdown-list] > * {
    width: 100%;
    box-sizing: border-box;
    min-height: 28px;
    font-size: var(--text-xs);
  }
  .${LANGUAGE_MENU_CLASS} [data-option-wrapper] {
    display: flex;
    width: 100%;
  }

  /* Nest the rows under the "Translated Languages" heading (PLAT-11563): the
     heading sits at a 6px inset, the rows at 14px (the shared row's 10px plus
     4px). Rows only — not the pinned header/footer chrome, which shares the
     same parent in both list shapes. */
  .${LANGUAGE_MENU_CLASS} [data-dropdown-list] > *,
  .${LANGUAGE_MENU_CLASS} > *:has(> [data-option-wrapper]) {
    padding-left: 14px;
  }
`;

const LanguagesPanel = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow-x: hidden;
  background: var(--background);
`;

const LanguagesPanelHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 44px;
  padding: 0 12px 0 6px;
  flex-shrink: 0;
  border-bottom: 1px solid var(--border-subtle);
`;

const LanguagesPanelTitle = styled.span`
  font-size: var(--text-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--foreground);
`;

const LanguagesPanelEmpty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 14px;
  font-size: var(--text-xs);
  color: var(--text-muted);
`;

/* The run chips inside the Languages tab (PLAT-11877). Above the list it
   takes the rows' inset; inside the empty state the column already spaces
   and insets its children. Hidden while no chip renders. */
const LanguagesPanelRun = styled(RunChipRow)`
  display: flex;
  padding: 10px 14px 0;
  flex-shrink: 0;

  &:empty {
    display: none;
  }

  ${LanguagesPanelEmpty} > & {
    padding: 0;
  }
`;

const LanguagesPanelList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  /* The menu class sets width: 100%; with the padding below that overran
     the pane and gave the modal a sideways scroll. */
  box-sizing: border-box;
  padding: 12px 8px 16px;
`;

// A row as the dropdown draws one: the whole row picks the language; the
// actions inside it stop their own clicks.
const LanguagesPanelRow = styled.div`
  display: flex;
  align-items: center;
  min-height: 36px;
  padding-right: 8px;
  border-radius: 6px;
  cursor: pointer;
  color: var(--text-secondary);

  &:hover,
  &:focus-visible {
    background: var(--surface-elevated);
    color: var(--foreground);
    outline: none;
  }
`;

/** PLAT-11877 — marks the Languages tab's list (beside the menu's class). */
const LANGUAGES_PANEL_CLASS = 'translation-languages-panel';

/* The tab reuses the menu's rules (the class above) but is a pane, not a
   popover: full width, no min-width, no lifted z-index, its own surface. Two
   classes outrank the menu's one, !important for !important. The context
   block takes a full radius and a gap below instead of bleeding to a popover's
   edges. */
const LanguagesPanelGlobalStyle = createGlobalStyle`
  .${LANGUAGE_MENU_CLASS}.${LANGUAGES_PANEL_CLASS} {
    width: 100% !important;
    min-width: 0 !important;
    z-index: auto !important;
    white-space: normal;
  }
  .${LANGUAGES_PANEL_CLASS} [data-context-block] {
    margin: 0 0 12px;
    padding: 4px;
    border-radius: 8px;
    border-bottom: none;
  }
  /* The pane's first row is the transcript toolbar's row (32px, no inset
     above), so "Current Language" sits level with the header row beside the
     player — the aspect-ratio dropdown — instead of a step below it. */
  .${LANGUAGES_PANEL_CLASS} {
    padding-top: 0;
  }
  .${LANGUAGES_PANEL_CLASS} > :first-child {
    display: flex;
    align-items: center;
    height: 32px;
    padding-top: 0;
    padding-bottom: 0;
    margin-bottom: 4px;
  }
`;

// The chip's code beside the globe (the dropdown's `OptionCode` look).
/* The first-translation button's label, beside the globe. */
const TranslateLabel = styled.span`
  margin-left: 6px;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
`;

const ToolbarLanguageCode = styled.span`
  margin-left: 4px;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  letter-spacing: 0.02em;
`;

// The line naming the translation being read, at the front of the
// transcript's below-header slot. It is context for the video, not a toolbar:
// no tint, no box — one row of secondary text, its two controls ghosts.
// The whole width of the row it sits in, padding included: the row is
// 100% wide and the pane clips, so the box has to be measured at its border
// or its last item (Review) ends past the right edge.
const TitleLine = styled.div`
  flex: 1 0 100%;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  padding: 2px 4px 6px 0;
`;

// An icon-only ghost (the design system's third tier): the arrow, its label
// in the tooltip and for the screen reader. Shared with the Languages
// panel's back arrow.
const TitleBack = styled.button`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: var(--radius-button);
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;

  &:hover {
    background: var(--border);
    color: var(--text-secondary);
  }
`;

const TitleText = styled.span`
  display: flex;
  align-items: baseline;
  gap: 6px;
  min-width: 0;
`;

const TitleName = styled.span`
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// Beside the name on the same line, separated by a middle dot drawn in CSS so
// the status text stays "Dubbed · Needs Update".
const TitleStatus = styled.span<{ $stale: boolean }>`
  flex-shrink: 0;
  font-size: 11px;
  white-space: nowrap;
  color: ${p => (p.$stale ? 'var(--accent)' : 'var(--text-muted)')};

  &::before {
    content: '·';
    margin-right: 6px;
    color: var(--text-muted);
  }
`;

// Review as a ghost: the row is secondary to the video, so its action is
// labelled but quiet, not the pane's green.
const TitleReviewButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 24px;
  padding: 0 8px;
  margin-left: auto;
  flex-shrink: 0;
  border: none;
  border-radius: var(--radius-button);
  background: transparent;
  color: var(--text-muted);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    background: var(--border);
    color: var(--text-secondary);
  }
`;

/**
 * PLAT-11662 — the two review entries a language carries wherever it is listed
 * (each "Switch To" row and the selected-language header): the pencil opens
 * Translation Review, the mic Dubbing Review (only for a language with a dub),
 * each for THAT language. Order beside the code: pencil, mic, then the kebab.
 * The row is itself a click target (it switches language), so each action
 * swallows its own click and keys.
 */
const RowReviewActions: React.FC<{
  onReviewTranslation?: () => void;
  onReviewDub?: () => void;
  /**
   * PLAT-11877 — the Languages tab's context block: the reading language's
   * Review is its one labelled action (a secondary button); the rows below
   * keep the icon, as in the dropdown.
   */
  labelled?: boolean;
}> = ({ onReviewTranslation, onReviewDub, labelled }) => {
  const rowAction = (fn: () => void) => ({
    onClick: (e: React.MouseEvent) => {
      e.stopPropagation();
      fn();
    },
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        fn();
      }
    },
  });
  return (
    <>
      {onReviewTranslation && labelled && (
        <ContextReviewButton
          type="button"
          aria-label="Review Translation"
          data-testid="languages-panel-review"
          data-option-review-pencil=""
          data-tooltip-id={LANGUAGE_MENU_TOOLTIP_ID}
          data-tooltip-content="Click to open Translation Review."
          {...rowAction(onReviewTranslation)}
        >
          <EditPenIcon size={13} />
          Review
        </ContextReviewButton>
      )}
      {onReviewTranslation && !labelled && (
        <RowIconAction
          role="button"
          tabIndex={0}
          $clickable
          aria-label="Review Translation"
          data-option-review-pencil=""
          data-tooltip-id={LANGUAGE_MENU_TOOLTIP_ID}
          data-tooltip-content="Click to open Translation Review."
          {...rowAction(onReviewTranslation)}
        >
          <EditPenIcon size={15} />
        </RowIconAction>
      )}
      {onReviewDub && (
        <RowIconAction
          role="button"
          tabIndex={0}
          $clickable
          aria-label="Review Dub"
          data-option-dub-mic=""
          data-tooltip-id={LANGUAGE_MENU_TOOLTIP_ID}
          data-tooltip-content="Has a dub. Click to open Dubbing Review."
          {...rowAction(onReviewDub)}
        >
          <MicStrokeIcon size={15} />
        </RowIconAction>
      )}
    </>
  );
};

/**
 * Dual-shape language entry. Renders:
 *   - a compact chip (globe + BCP 47 code) inside the toggle trigger
 *   - a two-column row (full language name left, BCP 47 code right-aligned)
 *     inside each dropdown option
 *
 * Both shapes are always emitted; CSS in `ToggleWrapper` hides whichever
 * isn't appropriate for the render context. This keeps the component
 * agnostic to `ItemDropdownSelect`'s internals (which always renders the
 * same `caption` node in both places).
 *
 * The `code` is shown in its canonical BCP 47 form ("fr-FR", "en-US" —
 * PLAT-11652: the menu used to collapse "fr-FR" to "fr", which read as a
 * different kind of code beside "en-US" and "pt-BR"). The underlying `value`
 * stays as given for analytics, store keys, and backend calls.
 */
const LanguageOption: React.FC<{
  code: string;
  label: string;
  hasDub?: boolean;
  stale?: boolean;
  /** Original row: render the real language name plus an "Original" badge,
   * mirroring the selected-voice header. */
  isOriginal?: boolean;
  /** The currently-displayed language — its name + code render gold (--accent),
   * the design system's dropdown selected-value treatment, so the active row is
   * distinct from the neutral hover fill (design-system.md). */
  selected?: boolean;
  /** Fires when the green mic is hovered — lets the host lazily load the dub
   * overlay for this language (PLAT-10732). */
  onDubHover?: () => void;
  /** PLAT-11662 — when set, every translated row carries a pencil that opens
   * Translation Review for this language, so the entry is discoverable from the
   * list itself and not only from the toolbar or the kebab. */
  onReviewTranslation?: () => void;
  /** PLAT-11662 — when set, the row's mic is a button that opens Dubbing
   * Review for this language. */
  onReviewDub?: () => void;
  /** When set, the stale indicator becomes a clickable button (hand cursor)
   * that regenerates this language's translation in place. Omitted → the
   * indicator is informational only (generation not available on this surface). */
  onRegenerate?: () => void;
  /** The row's actions kebab (PLAT-11563); the Original row passes none and
   * gets a same-width spacer so the code column stays aligned. */
  actions?: React.ReactNode;
}> = ({
  code,
  label,
  hasDub,
  stale,
  isOriginal,
  selected,
  onDubHover,
  onReviewTranslation,
  onReviewDub,
  onRegenerate,
  actions,
}) => {
  // Canonical BCP-47 casing for DISPLAY ("en-us" from the transcription record
  // → "en-US", matching the showcase codes in the rows below); the `value`
  // stays as given for store keys and analytics.
  const display = normalizeLangKey(code);
  return (
    <OptionWrapper data-option-wrapper="">
      <OptionChip data-option-chip="">
        <GlobeIconWrap aria-hidden="true">
          <GlobeIcon width="16" height="16" />
        </GlobeIconWrap>
        <OptionCode>{display}</OptionCode>
        {/* PLAT-11662: the chip no longer carries a dub mic — the toolbar's
            "Review Dub" microphone beside the dropdown says the same thing,
            and two mics in one row read as two controls. */}
      </OptionChip>
      <OptionRow data-option-row="">
        <OptionLabelWrap>
          <OptionLabel $selected={!!selected}>{label}</OptionLabel>
          {/* PLAT-11877 — the code reads as the name's meta, beside it, not
              as a column at the far end where it competed with the actions. */}
          <OptionRowCode $selected={!!selected}>{display}</OptionRowCode>
          {isOriginal && <OriginalNote>Original</OriginalNote>}
        </OptionLabelWrap>
        <OptionRowEnd>
          {stale &&
            (onRegenerate ? (
              // Follows CoverageBadge: the shared Badge is a div, so the
              // clickable wrapper is a focusable role="button" span rather
              // than a <button> around block content.
              <StaleAction
                role="button"
                tabIndex={0}
                aria-label="Needs update — regenerate translation"
                data-tooltip-id="translation-stale-tooltip"
                data-tooltip-content="This translation predates a transcript update and may not line up. Click to regenerate it."
                onClick={e => {
                  // Don't bubble to the row (which would switch language);
                  // just regenerate this translation in place.
                  e.stopPropagation();
                  onRegenerate();
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    onRegenerate();
                  }
                }}
              >
                <Badge
                  size="sm"
                  textColor="var(--text-secondary)"
                  borderColor="transparent"
                  backgroundColor="var(--surface-elevated)"
                >
                  Needs Update
                </Badge>
              </StaleAction>
            ) : (
              <StaleAction
                role="status"
                data-tooltip-id="translation-stale-tooltip"
                data-tooltip-content="This translation predates a transcript update and may not line up. Regenerate it from Transcript & Languages."
              >
                <Badge
                  size="sm"
                  textColor="var(--text-secondary)"
                  borderColor="transparent"
                  backgroundColor="var(--surface-elevated)"
                >
                  Needs Update
                </Badge>
              </StaleAction>
            ))}
          <RowReviewActions
            onReviewTranslation={onReviewTranslation}
            onReviewDub={onReviewDub}
          />
          {/* Without the review wired the mic is the passive dub-status glyph
              with its dub-info overlay (the pre-PLAT-11662 surface). */}
          {hasDub && !onReviewDub && (
            <RowIconAction
              $clickable={false}
              aria-label="Has a dub"
              data-option-dub-mic=""
              data-tooltip-id={DUB_MIC_TOOLTIP_ID}
              data-dub-lang={code}
              onMouseEnter={onDubHover}
              onFocus={onDubHover}
            >
              <MicrophoneIcon
                width="16"
                height="17"
                fillColor="var(--text-secondary)"
              />
            </RowIconAction>
          )}
          {actions ?? <RowActionsSpacer aria-hidden="true" />}
        </OptionRowEnd>
      </OptionRow>
    </OptionWrapper>
  );
};

/* Grid layout keeps the label on the left and code on the right inside
   a dropdown option. Using grid avoids the pitfalls of flex: 1 + nowrap
   inside an auto-sized DropdownItem where the row can overflow the
   menu panel's right edge — a fixed-minmax label column + auto code
   column always lays out inside the option's box. */
const OptionWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  font-variant-numeric: tabular-nums;
  width: 100%;
  box-sizing: border-box;
`;

const OptionChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const OptionRow = styled.span`
  /* Hidden by default — only revealed inside dropdown options (see
     ToggleWrapper's \`.Selected + div\` rule). */
  display: none;
  width: 100%;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 24px;
`;

const GlobeIconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  color: currentColor;
  opacity: 0.9;
`;

const OptionCode = styled.span`
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  letter-spacing: 0.02em;
`;

// Left grid cell of a language row: the caption plus (for Original) the
// "Original" badge, kept together at the start of the row.
const OptionLabelWrap = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const OptionLabel = styled.span<{ $selected?: boolean }>`
  /* Menu-row label on the --text-xs token. The selected row goes gold
     (--accent) — the DS dropdown selected-value treatment — so it's distinct
     from the neutral hover fill. */
  font-size: var(--text-xs);
  color: ${p => (p.$selected ? 'var(--accent)' : 'var(--foreground)')};
  font-weight: ${p =>
    p.$selected ? 'var(--font-weight-semibold)' : 'inherit'};
  white-space: nowrap;
`;

const OptionRowCode = styled.span<{ $selected?: boolean }>`
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  letter-spacing: 0.02em;
  color: ${p => (p.$selected ? 'var(--accent)' : 'var(--text-muted)')};
  font-variant-numeric: tabular-nums;
`;

/* The row's actions, packed against the kebab (PLAT-11877): pencil, mic where
   there is a dub, ⋯ — each a 20px box, 4px apart, so the cluster hugs the
   right edge the same way on every row. */
const OptionRowEnd = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  justify-self: end;
`;

/* Host for the "Needs Update" badge on a pre-stable-id (stale) translation
   (PLAT-11563). The badge itself is the shared Badge (the documented chip
   treatment); this span only carries the tooltip anchor and, when regeneration
   is available on the surface, the click/keyboard affordance. */
const StaleAction = styled.span<{ role?: string }>`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  border-radius: 999px;
  cursor: ${p => (p.role === 'button' ? 'pointer' : 'help')};

  /* Inside the row's own scale: same --text-xs as the label, normal weight, a
     soft --surface-elevated fill and NO border — outline chrome is reserved for
     the menu's two action buttons (Dub with AI Voice, Add Translation), so a
     status must not borrow it (PLAT-11563). */
  && > * {
    padding: 2px 8px;
    font-weight: var(--font-weight-normal);
    line-height: 1.2;
  }

  &:hover > *,
  &:focus-visible > * {
    color: ${p => (p.role === 'button' ? 'var(--foreground)' : 'inherit')};
  }
  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 1px;
  }
`;

/* A row's icon action (PLAT-11662): the Review Translation pencil and the
   Review Dub mic — or, without the review wired, the passive dub-status mic. */
// The design system's secondary button at `sm` size: outlined, neutral.
const ContextReviewButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 var(--btn-px-sm, 12px);
  border: 1px solid var(--border);
  border-radius: var(--radius-button);
  background: transparent;
  color: var(--foreground);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  cursor: pointer;

  &:hover {
    background: var(--surface-elevated);
    border-color: var(--text-muted);
  }
`;

const RowIconAction = styled.span<{ $clickable: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  color: var(--text-secondary);
  border-radius: var(--radius-button);
  ${props =>
    props.$clickable &&
    css`
      cursor: pointer;
      &:hover,
      &:focus-visible {
        color: var(--foreground);
        background: var(--surface-elevated);
      }
    `}
`;

/* Compact palette (PLAT-10544 toolbar consolidation) with a z lift: the
   empty-state Translate button also renders inside the clip-preview modal
   (~1,000,000 stack), where the compact style's resting z would hide the
   tooltip. Harmless in Creator Studio. */
const TRANSLATION_TOOLTIP_STYLE: React.CSSProperties = {
  ...COMPACT_TOOLTIP_STYLE,
  zIndex: 1000009,
};

const TranslateButton = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 4px;
  border: none;
  border-radius: var(--radius-button);
  background-color: transparent;
  color: var(--text-secondary);
  cursor: pointer;
  height: 100%;
  transition: all 150ms ease-out;

  &:hover:not(:disabled) {
    background-color: var(--surface-elevated);
    color: var(--foreground);
  }

  &:active:not(:disabled) {
    background-color: var(--gray-700);
    scale: 0.98;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const TranslateIcon = styled.span`
  display: inline-flex;
  align-items: center;
  color: currentColor;
`;

// Shares the toolbar button look with TranslateButton; declared after it so
// the styled(...) extension resolves the base component at module load.
const ReviewButton = styled(TranslateButton)`
  margin-left: 4px;

  /* Inert while Original is selected — muted, no hover/active feedback, same
     idiom as the redline toggle beside it (PLAT-11603). Keyed off
     aria-disabled so the tooltip keeps firing. */
  &[aria-disabled='true'] {
    color: var(--text-muted);
    cursor: default;
  }

  &[aria-disabled='true']:hover,
  &[aria-disabled='true']:active {
    background-color: transparent;
    color: var(--text-muted);
    scale: 1;
  }
`;
